import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { jobsAPI, assessmentsAPI, questionnairesAPI, phoneScreeningsAPI, challengesAPI } from '../../utils/api'; // eslint-disable-line no-unused-vars
import RecruiterHeader from './RecruiterHeader';
import RatingManager from './RatingManager';
import TagsManager from './TagsManager';
import NotesSection from './NotesSection';
import QuestionnaireResponseViewer from './QuestionnaireResponseViewer';
import PhoneScreeningScheduler from './PhoneScreeningScheduler';
import PhoneScreeningConduct from './PhoneScreeningConduct';
import ApprovalRequestModal from './ApprovalRequestModal';
import InterviewerAvailabilityManager from './InterviewerAvailabilityManager';
import ProcessStatus from './ProcessStatus';
import { getProcessStatus } from '../../utils/processStatus';
import { fetchAllPages } from '../../utils/fetchAllPages';
import ConfirmModal from '../shared/ConfirmModal';
import AddCandidateDrawerContent from './AddCandidateModal';
import JobEditor from './JobEditor';
import toastr from 'toastr';
import './ApplicationsList.css';
import { Drawer } from '../shared/DrawerManager';

/**
 * ApplicationsList Component
 *
 * View and manage all applications for a specific job posting:
 * - View candidate details (name, email, phone, etc.)
 * - Download resumes (authenticated, time-limited signed URLs)
 * - Schedule interviews
 * - Assign technical assessments/labs
 * - Track candidate pipeline (applied → interviewed → assessed → hired/rejected)
 * - Add recruiter notes
 */
const ApplicationsList = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [job, setJob] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRating, setFilterRating] = useState('all'); // 'all', '5', '4', '3', '2', '1'
  const [filterTags, setFilterTags] = useState([]); // Array of selected tag filters
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const [showAssessmentModal, setShowAssessmentModal] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [resumePreview, setResumePreview] = useState({ visible: false, url: null, filename: '', loading: false });
  const [resumeUploading, setResumeUploading] = useState(false);
  const [showJobEditor, setShowJobEditor] = useState(false);
  const [interviewDetails, setInterviewDetails] = useState(null);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [hasExistingInterview, setHasExistingInterview] = useState(false); // UX-38: Track if interview exists
  const [showQuestionnaireModal, setShowQuestionnaireModal] = useState(false);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [loadingQuestionnaires, setLoadingQuestionnaires] = useState(false);
  const [bulkQuestionnaireTargets, setBulkQuestionnaireTargets] = useState([]);
  const [selectedQuestionnaireForSend, setSelectedQuestionnaireForSend] = useState(null);
  const [extraQuestions, setExtraQuestions] = useState([]);
  const [sendingQuestionnaire, setSendingQuestionnaire] = useState(false);
  const canViewQuestionnaireResponses = (application) =>
    Boolean(
      application?.status === 'questionnaire_completed' ||
      application?.questionnaireCompletedAt ||
      application?.questionnaireResponseId
    );
  const [editingProfile, setEditingProfile] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);
  const [showResponseViewer, setShowResponseViewer] = useState(false);
  const [showPhoneScreeningScheduler, setShowPhoneScreeningScheduler] = useState(false);
  const [showPhoneScreeningConduct, setShowPhoneScreeningConduct] = useState(false);
  const [selectedPhoneScreening, setSelectedPhoneScreening] = useState(null); // eslint-disable-line no-unused-vars
  const [phoneScreening, setPhoneScreening] = useState(null);
  const [showApprovalRequestModal, setShowApprovalRequestModal] = useState(false);
  const [showAvailabilityManager, setShowAvailabilityManager] = useState(false);
  
  // NEW: Bulk selection state
  const [selectedApplicationIds, setSelectedApplicationIds] = useState([]);
  const [showGroupInterviewModal, setShowGroupInterviewModal] = useState(false);
  
  // NEW: Tab state for candidate profile drawer
  const [activeProfileTab, setActiveProfileTab] = useState('overview');
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  
  // Add candidate modal state
  const [showAddCandidateModal, setShowAddCandidateModal] = useState(false);
  const [roleFitNotes, setRoleFitNotes] = useState('');
  const [moveTargetJobId, setMoveTargetJobId] = useState('');
  const [availableMoveJobs, setAvailableMoveJobs] = useState([]);
  const [loadingMoveJobs, setLoadingMoveJobs] = useState(false);
  const [processingRoleFit, setProcessingRoleFit] = useState(false);

  // Status change confirmation state
  const [statusChangeConfirmation, setStatusChangeConfirmation] = useState({
    isOpen: false,
    applicationId: null,
    currentStatus: null,
    newStatus: null,
    applicationName: null,
    force: false
  });
  
  // Ref to track drawer content for scroll preservation
  const drawerContentRef = useRef(null);
  const scrollPositionRef = useRef(0);

  useEffect(() => {
    loadApplications();
  }, [jobId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedApplication && selectedApplication._id) {
      loadInterviewDetails(selectedApplication._id);
    } else {
      setInterviewDetails(null);
    }
  }, [selectedApplication?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadApplications = async () => {
    try {
      setLoading(true);
      setError('');

      // Load job details and applications. The applications endpoint is
      // paginated, so every page is fetched — search, filters and bulk actions
      // below all run over this list client-side and would otherwise only ever
      // see the first page.
      const [jobResponse, allApplications] = await Promise.all([
        jobsAPI.getById(jobId),
        fetchAllPages(
          (params) => jobsAPI.getApplications(jobId, params),
          (data) => data.applications
        )
      ]);

      setJob(jobResponse.data.job);
      setApplications(allApplications);
    } catch (err) {
      console.error('[ApplicationsList] Error loading data:', err);
      setError(err.response?.data?.error || 'Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableMoveJobs = async () => {
    try {
      setLoadingMoveJobs(true);
      const response = await jobsAPI.getAll({ limit: 250 });
      const jobs = response?.data?.jobs || [];
      setAvailableMoveJobs(jobs.filter(j => j._id !== jobId));
    } catch (err) {
      console.error('[ApplicationsList] Failed to load jobs for role move:', err);
      setAvailableMoveJobs([]);
    } finally {
      setLoadingMoveJobs(false);
    }
  };

  // Helper: resolve resume blob from API (handles both signed-URL JSON and direct-stream responses)
  const fetchResumeBlob = async (applicationId) => {
    const response = await jobsAPI.downloadResume(jobId, applicationId);
    const contentType = response.headers['content-type'] || '';

    // If server returned a signed URL (JSON response), fetch the actual file from that URL
    if (contentType.includes('application/json')) {
      const text = await response.data.text();
      const json = JSON.parse(text);
      if (json.downloadUrl) {
        const fileRes = await fetch(json.downloadUrl);
        const blob = await fileRes.blob();
        return blob;
      }
    }

    // Direct stream — response.data is already a Blob
    return new Blob([response.data], { type: contentType || 'application/pdf' });
  };

  const handleDownloadResume = async (applicationId, filename) => {
    try {
      const blob = await fetchResumeBlob(applicationId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'resume.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toastr.success(`Downloaded ${filename}`);
    } catch (err) {
      console.error('[ApplicationsList] Resume download failed:', err);
      toastr.error('Failed to download resume');
    }
  };

  const handlePreviewResume = async (applicationId, filename) => {
    setResumePreview({ visible: true, url: null, filename: filename || 'resume.pdf', loading: true });
    try {
      const blob = await fetchResumeBlob(applicationId);
      const url = window.URL.createObjectURL(blob);
      setResumePreview({ visible: true, url, filename: filename || 'resume.pdf', loading: false });
    } catch (err) {
      console.error('[ApplicationsList] Resume preview failed:', err);
      toastr.error('Failed to load resume preview');
      setResumePreview({ visible: false, url: null, filename: '', loading: false });
    }
  };

  const closeResumePreview = () => {
    if (resumePreview.url) window.URL.revokeObjectURL(resumePreview.url);
    setResumePreview({ visible: false, url: null, filename: '', loading: false });
  };

  // Close resume preview on Escape key
  useEffect(() => {
    if (!resumePreview.visible) return;
    const handleEsc = (e) => { if (e.key === 'Escape') closeResumePreview(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [resumePreview.visible]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleResumeUpload = async (applicationId, file) => {
    if (!file) return;
    setResumeUploading(true);
    try {
      await jobsAPI.uploadResume(jobId, applicationId, file);
      toastr.success('Resume uploaded successfully');
      // Refresh applications to show updated resume
      await loadApplications();
    } catch (err) {
      console.error('[ApplicationsList] Resume upload failed:', err);
      toastr.error(err.response?.data?.error || 'Failed to upload resume');
    } finally {
      setResumeUploading(false);
    }
  };

  const handleScheduleInterview = async (application) => {
    // Check process status - block if rejected/withdrawn
    const process = getProcessStatus(application);
    if (process.status === 'blocked') {
      toastr.error(process.message || 'Cannot schedule interview for this candidate');
      return;
    }

    setSelectedApplication(application);
    
    // UX-38: Check if interview exists and load it
    try {
      const response = await jobsAPI.getInterviewDetails(jobId, application._id);
      if (response.data.interview) {
        setInterviewDetails(response.data.interview);
        setHasExistingInterview(true);
      } else {
        setInterviewDetails(null);
        setHasExistingInterview(false);
      }
    } catch (err) {
      // Interview doesn't exist or error - assume no interview
      setInterviewDetails(null);
      setHasExistingInterview(false);
    }
    
    setShowInterviewModal(true);
  };

  const handleAssignAssessment = (application) => {
    setSelectedApplication(application);
    setShowAssessmentModal(true);
  };

  const handleSendQuestionnaire = async (application) => {
    setSelectedApplication(application);
    setBulkQuestionnaireTargets([]);
    setLoadingQuestionnaires(true);
    try {
      const result = await questionnairesAPI.getAll({ jobPostingId: jobId, status: 'active' });
      setQuestionnaires(result.data.questionnaires || []);
      setShowQuestionnaireModal(true);
    } catch (error) {
      console.error('Failed to load questionnaires:', error);
      toastr.error('Failed to load questionnaires');
    } finally {
      setLoadingQuestionnaires(false);
    }
  };

  const handleBulkSendQuestionnaire = async () => {
    if (selectedApplicationIds.length === 0) {
      toastr.error('Please select at least one candidate');
      return;
    }
    setBulkQuestionnaireTargets(selectedApplicationIds);
    setSelectedApplication(null);
    setLoadingQuestionnaires(true);
    try {
      const result = await questionnairesAPI.getAll({ jobPostingId: jobId, status: 'active' });
      setQuestionnaires(result.data.questionnaires || []);
      setShowQuestionnaireModal(true);
    } catch (error) {
      console.error('Failed to load questionnaires:', error);
      toastr.error('Failed to load questionnaires');
    } finally {
      setLoadingQuestionnaires(false);
    }
  };

  const handleSchedulePhoneScreening = async (application) => {
    setSelectedApplication(application);
    setShowPhoneScreeningScheduler(true);
  };

  const handleConductPhoneScreening = async (application) => {
    setSelectedApplication(application);
    try {
      // Load existing phone screening for this application
      const response = await phoneScreeningsAPI.getAll({ jobApplicationId: application._id });
      const screenings = response.data.screenings || [];
      if (screenings.length > 0) {
        const latestScreening = screenings[0];
        setPhoneScreening(latestScreening);
        setShowPhoneScreeningConduct(true);
      } else {
        toastr.error('No phone screening found for this candidate');
      }
    } catch (error) {
      console.error('Failed to load phone screening:', error);
      toastr.error('Failed to load phone screening');
    }
  };

  const handleUpdateStatus = async (applicationId, newStatus, currentStatus = null, applicationName = null, force = false) => {
    // Terminal states that require confirmation
    const terminalStates = ['rejected', 'withdrawn', 'hired', 'declined'];
    const isChangingToTerminal = terminalStates.includes(newStatus);
    const isChangingFromTerminal = currentStatus && terminalStates.includes(currentStatus);
    const requiresConfirmation = isChangingToTerminal || isChangingFromTerminal;

    // If confirmation is needed and not already confirmed, show modal
    if (requiresConfirmation && !force && !statusChangeConfirmation.isOpen) {
      // Find application to get current status and name if not provided
      const app = applications.find(a => a._id === applicationId);
      const appCurrentStatus = currentStatus || app?.status;
      const appName = applicationName || (app ? `${app.candidateInfo.firstName} ${app.candidateInfo.lastName}` : 'this candidate');

      setStatusChangeConfirmation({
        isOpen: true,
        applicationId,
        currentStatus: appCurrentStatus,
        newStatus,
        applicationName: appName,
        force: isChangingFromTerminal // If changing FROM terminal, allow force/revert
      });
      return;
    }

    // Proceed with status update
    try {
      await jobsAPI.updateApplicationStatus(jobId, applicationId, newStatus, null, force || isChangingFromTerminal);
      toastr.success(`Status updated to ${newStatus}${force || isChangingFromTerminal ? ' (reverted)' : ''}`);
      loadApplications();
      // Reset confirmation state
      setStatusChangeConfirmation({
        isOpen: false,
        applicationId: null,
        currentStatus: null,
        newStatus: null,
        applicationName: null,
        force: false
      });
    } catch (err) {
      console.error('[ApplicationsList] Status update failed:', err);
      // Handle nested error structure: { error: { message: '...', status: 400 } }
      const errorMessage = err.response?.data?.error?.message || 
                          err.response?.data?.error || 
                          err.message || 
                          'Failed to update status';
      toastr.error(errorMessage);
      // Reset confirmation state on error
      setStatusChangeConfirmation({
        isOpen: false,
        applicationId: null,
        currentStatus: null,
        newStatus: null,
        applicationName: null,
        force: false
      });
    }
  };

  const confirmStatusChange = () => {
    if (statusChangeConfirmation.applicationId && statusChangeConfirmation.newStatus) {
      handleUpdateStatus(
        statusChangeConfirmation.applicationId,
        statusChangeConfirmation.newStatus,
        statusChangeConfirmation.currentStatus,
        statusChangeConfirmation.applicationName,
        statusChangeConfirmation.force
      );
    }
  };

  const loadInterviewDetails = async (applicationId) => {
    try {
      const response = await jobsAPI.getInterviewDetails(jobId, applicationId);
      setInterviewDetails(response.data.interview);
    } catch (err) {
      console.error('[ApplicationsList] Failed to load interview details:', err);
      setInterviewDetails(null);
    }
  };

  const handleSendReminder = async () => {
    if (!interviewDetails) return;

    try {
      setSendingReminder(true);
      const response = await jobsAPI.sendInterviewReminder(interviewDetails._id);
      toastr.success(response.data.message || 'Reminder sent successfully!');
    } catch (err) {
      console.error('[ApplicationsList] Failed to send reminder:', err);
      toastr.error(err.response?.data?.error || 'Failed to send reminder');
    } finally {
      setSendingReminder(false);
    }
  };

  // Handle rating update
  const handleRatingUpdate = async (ratings, overallRating) => {
    if (!selectedApplication) return;

    // Preserve scroll position in drawer - capture BEFORE any async operations
    const currentScroll = drawerContentRef.current ? drawerContentRef.current.scrollTop : 0;
    scrollPositionRef.current = currentScroll;
    
    // Store scroll in data attribute for scroll event handler
    if (drawerContentRef.current) {
      drawerContentRef.current.setAttribute('data-saved-scroll', currentScroll.toString());
    }

    try {
      await jobsAPI.updateRating(jobId, selectedApplication._id, ratings, overallRating);
      toastr.success('Rating updated successfully');
      
      // Update selectedApplication in place to avoid drawer re-render
      setSelectedApplication(prev => ({
        ...prev,
        ratings: { ...ratings, overall: overallRating },
        rating: overallRating // Keep backward compatibility
      }));
      
      // Update applications list in place instead of full reload
      setApplications(prev => prev.map(app => 
        app._id === selectedApplication._id 
          ? { ...app, ratings: { ...ratings, overall: overallRating }, rating: overallRating }
          : app
      ));
      
      // Aggressively restore scroll - multiple attempts
      const restoreScroll = () => {
        if (drawerContentRef.current && currentScroll > 0) {
          drawerContentRef.current.scrollTop = currentScroll;
          drawerContentRef.current.setAttribute('data-saved-scroll', currentScroll.toString());
        }
      };
      
      // Immediate restore
      restoreScroll();
      // After paint
      requestAnimationFrame(restoreScroll);
      // After React render
      setTimeout(restoreScroll, 0);
      // Fallback
      setTimeout(restoreScroll, 50);
      setTimeout(restoreScroll, 100);
    } catch (err) {
      console.error('[ApplicationsList] Failed to update rating:', err);
      toastr.error(err.response?.data?.error || 'Failed to update rating');
    }
  };

  // Handle tags update
  const handleTagsUpdate = async (tags) => {
    if (!selectedApplication) return;

    // Preserve scroll position in drawer
    const currentScroll = drawerContentRef.current ? drawerContentRef.current.scrollTop : 0;
    scrollPositionRef.current = currentScroll;
    
    if (drawerContentRef.current) {
      drawerContentRef.current.setAttribute('data-saved-scroll', currentScroll.toString());
    }

    try {
      await jobsAPI.updateTags(jobId, selectedApplication._id, tags);
      toastr.success('Tags updated successfully');
      
      // Update selectedApplication in place to avoid drawer re-render
      setSelectedApplication(prev => ({
        ...prev,
        tags: tags
      }));
      
      // Update applications list in place instead of full reload
      setApplications(prev => prev.map(app => 
        app._id === selectedApplication._id 
          ? { ...app, tags: tags }
          : app
      ));
      
      // Aggressively restore scroll
      const restoreScroll = () => {
        if (drawerContentRef.current && currentScroll > 0) {
          drawerContentRef.current.scrollTop = currentScroll;
          drawerContentRef.current.setAttribute('data-saved-scroll', currentScroll.toString());
        }
      };
      restoreScroll();
      requestAnimationFrame(restoreScroll);
      setTimeout(restoreScroll, 0);
      setTimeout(restoreScroll, 50);
      setTimeout(restoreScroll, 100);
    } catch (err) {
      console.error('[ApplicationsList] Failed to update tags:', err);
      toastr.error(err.response?.data?.error || 'Failed to update tags');
    }
  };

  // Handle note addition
  const handleAddNote = async (note) => {
    if (!selectedApplication) return;

    // Preserve scroll position in drawer
    const currentScroll = drawerContentRef.current ? drawerContentRef.current.scrollTop : 0;
    scrollPositionRef.current = currentScroll;
    
    if (drawerContentRef.current) {
      drawerContentRef.current.setAttribute('data-saved-scroll', currentScroll.toString());
    }

    try {
      const response = await jobsAPI.addApplicationNote(jobId, selectedApplication._id, note);
      toastr.success('Note added successfully');
      
      // Update selectedApplication in place to avoid drawer re-render
      if (response.data && response.data.application) {
        setSelectedApplication(prev => ({
          ...prev,
          recruiterNotes: response.data.application.recruiterNotes || prev.recruiterNotes
        }));
      }
      
      // Aggressively restore scroll
      const restoreScroll = () => {
        if (drawerContentRef.current && currentScroll > 0) {
          drawerContentRef.current.scrollTop = currentScroll;
          drawerContentRef.current.setAttribute('data-saved-scroll', currentScroll.toString());
        }
      };
      restoreScroll();
      requestAnimationFrame(restoreScroll);
      setTimeout(restoreScroll, 0);
      setTimeout(restoreScroll, 50);
      setTimeout(restoreScroll, 100);
    } catch (err) {
      console.error('[ApplicationsList] Failed to add note:', err);
      toastr.error(err.response?.data?.error || 'Failed to add note');
    }
  };

  // Handle comment addition
  const handleAddComment = async (comment, mentions) => {
    if (!selectedApplication) return;

    // Debug logging
    console.log('[ApplicationsList] handleAddComment called:', {
      comment: comment?.substring(0, 50),
      mentions: mentions,
      mentionsType: typeof mentions,
      mentionsIsArray: Array.isArray(mentions),
      mentionsLength: mentions?.length
    });

    // Preserve scroll position in drawer
    const currentScroll = drawerContentRef.current ? drawerContentRef.current.scrollTop : 0;
    scrollPositionRef.current = currentScroll;
    
    if (drawerContentRef.current) {
      drawerContentRef.current.setAttribute('data-saved-scroll', currentScroll.toString());
    }

    try {
      // Pass explicit mentions to ensure correct user selection
      console.log('[ApplicationsList] Calling API with mentions:', mentions);
      const response = await jobsAPI.addComment(jobId, selectedApplication._id, comment, mentions);
      toastr.success('Comment added successfully');
      
      // Update selectedApplication in place to avoid drawer re-render
      if (response.data && response.data.application) {
        setSelectedApplication(prev => ({
          ...prev,
          internalComments: response.data.application.internalComments || prev.internalComments
        }));
      }
      
      // Aggressively restore scroll
      const restoreScroll = () => {
        if (drawerContentRef.current && currentScroll > 0) {
          drawerContentRef.current.scrollTop = currentScroll;
          drawerContentRef.current.setAttribute('data-saved-scroll', currentScroll.toString());
        }
      };
      restoreScroll();
      requestAnimationFrame(restoreScroll);
      setTimeout(restoreScroll, 0);
      setTimeout(restoreScroll, 50);
      setTimeout(restoreScroll, 100);
    } catch (err) {
      console.error('[ApplicationsList] Failed to add comment:', err);
      toastr.error(err.response?.data?.error || 'Failed to add comment');
    }
  };

  const handleViewProfile = async (app) => {
    // Load full application details with all populated fields
    try {
      const response = await jobsAPI.getApplication(jobId, app._id);
      setSelectedApplication(response.data.application);
    } catch (err) {
      // Fallback to app data if API fails
      setSelectedApplication(app);
    }
    
    setActiveProfileTab('overview'); // Reset to overview tab
    setRoleFitNotes('');
    setMoveTargetJobId('');
    setShowProfileModal(true);
    loadAvailableMoveJobs();
    
    // UX-38: Check if interview exists for this application
    try {
      const response = await jobsAPI.getInterviewDetails(jobId, app._id);
      if (response.data.interview) {
        setInterviewDetails(response.data.interview);
        setHasExistingInterview(true);
      } else {
        setInterviewDetails(null);
        setHasExistingInterview(false);
      }
    } catch (err) {
      // Interview doesn't exist or error - assume no interview
      setInterviewDetails(null);
      setHasExistingInterview(false);
    }
    // Load interview details if interview is scheduled
    if (app.status === 'interview') {
      loadInterviewDetails(app._id);
    } else {
      setInterviewDetails(null);
    }
  };

  const handleRoleFitDecision = async (action) => {
    if (!selectedApplication?._id) {
      toastr.error('No application selected');
      return;
    }

    if (action === 'move' && !moveTargetJobId) {
      toastr.error('Select a target role before moving candidate');
      return;
    }

    try {
      setProcessingRoleFit(true);
      await jobsAPI.submitRoleFitDecision(
        jobId,
        selectedApplication._id,
        action,
        action === 'move' ? moveTargetJobId : null,
        roleFitNotes
      );

      const refreshed = await jobsAPI.getApplication(jobId, selectedApplication._id);
      const updatedApplication = refreshed?.data?.application;

      if (updatedApplication) {
        setSelectedApplication(updatedApplication);
        setApplications(prev => prev.map(app =>
          app._id === updatedApplication._id
            ? { ...app, status: updatedApplication.status, roleFitDecision: updatedApplication.roleFitDecision }
            : app
        ));
      }

      setMoveTargetJobId('');
      setRoleFitNotes('');
      toastr.success(action === 'move'
        ? 'Candidate marked unfit and moved to selected role'
        : 'Candidate marked unfit and retained for other roles');
    } catch (err) {
      console.error('[ApplicationsList] Failed role fit decision:', err);
      toastr.error(err.response?.data?.error || 'Failed to save role fit decision');
    } finally {
      setProcessingRoleFit(false);
    }
  };

  // NEW: Bulk selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = filteredApplications.map(app => app._id);
      setSelectedApplicationIds(allIds);
    } else {
      setSelectedApplicationIds([]);
    }
  };

  const handleSelectApplication = (applicationId) => {
    setSelectedApplicationIds(prev => {
      if (prev.includes(applicationId)) {
        return prev.filter(id => id !== applicationId);
      } else {
        return [...prev, applicationId];
      }
    });
  };

  const handleGroupInterview = () => {
    if (selectedApplicationIds.length === 0) {
      toastr.error('Please select at least one candidate');
      return;
    }
    setShowGroupInterviewModal(true);
  };

  const handleBulkStatusUpdate = async (newStatus) => {
    if (selectedApplicationIds.length === 0) {
      toastr.error('Please select at least one candidate');
      return;
    }
    
    const confirmation = window.confirm(
      `Are you sure you want to update ${selectedApplicationIds.length} application(s) to "${newStatus}"?`
    );
    
    if (!confirmation) return;
    
    try {
      // Use bulk update endpoint (FIX: P1 - More efficient than Promise.all)
      const response = await jobsAPI.bulkUpdateApplicationStatus(
        jobId,
        selectedApplicationIds,
        newStatus,
        null // reason - optional
      );
      
      if (response.data.success) {
        const { updated, failed } = response.data.results;
        if (failed.length === 0) {
          toastr.success(`Successfully updated ${updated.length} application(s) to "${newStatus}"`);
        } else {
          toastr.warning(`Updated ${updated.length}, failed ${failed.length}. Check console for details.`);
          console.warn('[ApplicationsList] Bulk update partial failure:', failed);
        }
      }
      
      setSelectedApplicationIds([]);
      loadApplications();
    } catch (err) {
      console.error('[ApplicationsList] Bulk update failed:', err);
      toastr.error(err.response?.data?.error || 'Failed to update applications');
    }
  };

  const filteredApplications = applications.filter(app => {
    // Filter by status
    if (filterStatus !== 'all' && app.status !== filterStatus) {
      return false;
    }

    // Filter by rating
    if (filterRating !== 'all') {
      const overallRating = app.ratings?.overall || 0;
      if (parseInt(filterRating) !== overallRating) {
        return false;
      }
    }

    // Filter by tags
    if (filterTags.length > 0) {
      const appTags = app.tags || [];
      const hasAllTags = filterTags.every(tag => appTags.includes(tag));
      if (!hasAllTags) {
        return false;
      }
    }

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = (
        app.candidateInfo.firstName?.toLowerCase().includes(searchLower) ||
        app.candidateInfo.lastName?.toLowerCase().includes(searchLower) ||
        app.candidateInfo.email?.toLowerCase().includes(searchLower)
      );
      if (!matchesSearch) {
        return false;
      }
    }

    return true;
  });

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'new': 'status-new',
      'reviewing': 'status-pending',
      'shortlisted': 'status-pending',
      'screening_call': 'status-progress',
      'screening_passed': 'status-completed',
      'questionnaire_sent': 'status-progress',
      'questionnaire_completed': 'status-completed',
      'interview': 'status-scheduled',
      'assessment_sent': 'status-progress',
      'assessment_completed': 'status-completed',
      'offer': 'status-success',
      'accepted': 'status-success',
      'hired': 'status-success',
      'rejected': 'status-rejected',
      'declined': 'status-rejected',
      'withdrawn': 'status-rejected'
    };
    return statusMap[status] || 'status-default';
  };

  if (loading) {
    return (
      <div className="recruiter-container">
        <RecruiterHeader user={user} />
        <div className="loading-state">Loading applications...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="recruiter-container">
        <RecruiterHeader user={user} />
        <div className="error-state">{error}</div>
      </div>
    );
  }

  return (
    <div className="recruiter-container">
      <RecruiterHeader user={user} />

      <div className="applications-content">
        {/* Header */}
        <div className="applications-header">
          <div>
            <h1>{job?.title}</h1>
            <p className="job-meta">
              {job?.location} • {applications.length} {applications.length === 1 ? 'application' : 'applications'}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <button
              className="btn-primary"
              onClick={() => setShowJobEditor(true)}
              title="Edit this job posting"
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20h9"/>
                <path d="M16.5 3.5a2.1 2.1 0 1 1 3 3L7 19l-4 1 1-4Z"/>
              </svg>
              Edit This Job
            </button>
            <button
              className="btn-primary"
              onClick={() => setShowAddCandidateModal(true)}
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              Add Candidate
            </button>
            <button
              className="btn-secondary"
              onClick={() => navigate('/recruiter/jobs')}
            >
              ← Back to Jobs
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="applications-filters">
          <input
            type="text"
            className="search-input"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          <select
            className="status-filter"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{ minWidth: '150px' }}
          >
            <option value="all">All Statuses</option>
            <option value="new">New</option>
            <option value="reviewing">Reviewing</option>
            <option value="shortlisted">Shortlisted</option>
            <option value="screening_call">Screening Call</option>
            <option value="screening_passed">Screening Passed</option>
            <option value="questionnaire_sent">Questionnaire Sent</option>
            <option value="questionnaire_completed">Questionnaire Done</option>
            <option value="interview">Interview</option>
            <option value="assessment_sent">Assessment Sent</option>
            <option value="assessment_completed">Assessment Completed</option>
            <option value="offer">Offer</option>
            <option value="accepted">Accepted</option>
            <option value="declined">Declined</option>
            <option value="hired">Hired</option>
            <option value="rejected">Rejected</option>
            <option value="withdrawn">Withdrawn</option>
          </select>
          <select
            className="status-filter"
            value={filterRating}
            onChange={(e) => setFilterRating(e.target.value)}
            style={{ minWidth: '150px' }}
          >
            <option value="all">All Ratings</option>
            <option value="5">5 Stars</option>
            <option value="4">4+ Stars</option>
            <option value="3">3+ Stars</option>
            <option value="2">2+ Stars</option>
            <option value="1">1+ Stars</option>
          </select>
          {/* Tags Filter - Multi-select */}
          <div style={{ position: 'relative', minWidth: '180px' }}>
            <select
              className="status-filter"
              value=""
              onChange={(e) => {
                if (e.target.value && !filterTags.includes(e.target.value)) {
                  setFilterTags([...filterTags, e.target.value]);
                }
              }}
              style={{ width: '100%' }}
            >
              <option value="">Filter by Tags...</option>
              {(() => {
                // Get all unique tags from applications
                const allTags = new Set();
                applications.forEach(app => {
                  if (app.tags && Array.isArray(app.tags)) {
                    app.tags.forEach(tag => allTags.add(tag));
                  }
                });
                return Array.from(allTags).sort().map(tag => (
                  <option key={tag} value={tag}>{tag}</option>
                ));
              })()}
            </select>
            {filterTags.length > 0 && (
              <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                {filterTags.map(tag => (
                  <span
                    key={tag}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      padding: '0.25rem 0.5rem',
                      background: '#EFF6FF',
                      color: '#1E40AF',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      fontWeight: '500'
                    }}
                  >
                    {tag}
                    <button
                      onClick={() => setFilterTags(filterTags.filter(t => t !== tag))}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#1E40AF',
                        cursor: 'pointer',
                        padding: 0,
                        fontSize: '0.875rem',
                        lineHeight: '1'
                      }}
                      title="Remove filter"
                    >
                      ×
                    </button>
                  </span>
                ))}
                <button
                  onClick={() => setFilterTags([])}
                  style={{
                    padding: '0.25rem 0.5rem',
                    background: '#F3F4F6',
                    color: '#374151',
                    border: '1px solid #D1D5DB',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    cursor: 'pointer'
                  }}
                >
                  Clear All
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Applications Table */}
        {/* Bulk Actions Menu - Only shows when candidates are selected */}
        {selectedApplicationIds.length > 0 && (
          <div style={{
            background: '#EEF2FF',
            border: '1px solid #C7D2FE',
            borderRadius: '8px',
            padding: '1rem 1.5rem',
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div style={{
                background: '#4F46E5',
                color: 'white',
                borderRadius: '4px',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '600',
                fontSize: '0.875rem'
              }}>
                {selectedApplicationIds.length}
              </div>
              <div>
                <div style={{ fontSize: '0.9375rem', fontWeight: '600', color: '#1E293B' }}>
                  {selectedApplicationIds.length} candidate{selectedApplicationIds.length !== 1 ? 's' : ''} selected
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <button
                onClick={handleGroupInterview}
                style={{
                  background: '#4F46E5',
                  color: 'white',
                  border: 'none',
                  padding: '0.625rem 1.25rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#4338CA';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#4F46E5';
                }}
              >
                Schedule Group Interview
              </button>
              <button
                onClick={() => handleBulkStatusUpdate('shortlisted')}
                style={{
                  background: '#10B981',
                  color: 'white',
                  border: 'none',
                  padding: '0.625rem 1.25rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#059669';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#10B981';
                }}
              >
                Shortlist All
              </button>
              <button
                onClick={handleBulkSendQuestionnaire}
                style={{
                  background: '#8B5CF6',
                  color: 'white',
                  border: 'none',
                  padding: '0.625rem 1.25rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#7C3AED';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#8B5CF6';
                }}
              >
                Send Questionnaire
              </button>
              <button
                onClick={() => handleBulkStatusUpdate('rejected')}
                style={{
                  background: '#EF4444',
                  color: 'white',
                  border: 'none',
                  padding: '0.625rem 1.25rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#DC2626';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#EF4444';
                }}
              >
                Reject All
              </button>
              <button
                onClick={() => setSelectedApplicationIds([])}
                style={{
                  background: 'white',
                  color: '#64748B',
                  border: '1px solid #E2E8F0',
                  padding: '0.625rem 1.25rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#F8FAFC';
                  e.target.style.borderColor = '#CBD5E1';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'white';
                  e.target.style.borderColor = '#E2E8F0';
                }}
              >
                Clear Selection
              </button>
            </div>
          </div>
        )}

        {filteredApplications.length === 0 ? (
          <div className="empty-state">
            <p>No applications found</p>
          </div>
        ) : (
          <div className="applications-table">
            <table>
              <thead>
                <tr>
                  <th style={{ width: '40px', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={selectedApplicationIds.length === filteredApplications.length && filteredApplications.length > 0}
                      onChange={handleSelectAll}
                      style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                    />
                  </th>
                  <th>Candidate</th>
                  <th>Resume</th>
                  <th>Experience</th>
                  <th>Assessment Labs</th>
                  <th>Applied</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredApplications.map(app => (
                  <tr key={app._id}>
                    <td style={{ textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={selectedApplicationIds.includes(app._id)}
                        onChange={() => handleSelectApplication(app._id)}
                        style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                      />
                    </td>
                    <td>
                      <div style={{ fontSize: '0.95rem', fontWeight: '500', color: '#111827', marginBottom: '0.25rem' }}>
                        {app.candidateInfo.firstName} {app.candidateInfo.lastName}
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#6B7280', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <span>{app.candidateInfo.email}</span>
                        {(() => {
                          const verificationStatus = app.verificationStatus || 
                            (app.emailVerified === true && app.phoneVerified === true ? 'verified' :
                             app.emailVerified === true || app.phoneVerified === true ? 'partially_verified' :
                             'unverified');
                          
                          if (verificationStatus === 'verified') {
                            return (
                              <span
                                title="Email and phone verified"
                                style={{
                                  background: '#D1FAE5',
                                  color: '#065F46',
                                  fontSize: '0.6875rem',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  fontWeight: '600',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.3px'
                                }}
                              >
                                Verified
                              </span>
                            );
                          } else if (verificationStatus === 'partially_verified') {
                            return (
                              <span
                                title="Partially verified (email or phone only)"
                                style={{
                                  background: '#FEF3C7',
                                  color: '#92400E',
                                  fontSize: '0.6875rem',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  fontWeight: '600',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.3px'
                                }}
                              >
                                Partially Verified
                              </span>
                            );
                          } else {
                            return (
                              <span
                                title="Email and phone not verified"
                                style={{
                                  background: '#FEE2E2',
                                  color: '#991B1B',
                                  fontSize: '0.6875rem',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  fontWeight: '600',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.3px'
                                }}
                              >
                                Unverified
                              </span>
                            );
                          }
                        })()}
                      </div>
                      {app.candidateInfo.phone && (
                        <div className="text-muted" style={{ fontSize: '0.85rem', marginBottom: '0.25rem' }}>
                          {app.candidateInfo.phone}
                        </div>
                      )}

                      {/* Source badge */}
                      {(app.source || app.manuallyAdded) && (
                        <div style={{ marginTop: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.375rem', flexWrap: 'wrap' }}>
                          {app.source && app.source !== 'direct' && (
                            <span className={`source-badge source-${app.source}`}>
                              {app.source === 'linkedin' ? 'LinkedIn' :
                               app.source === 'naukri' ? 'Naukri' :
                               app.source === 'indeed' ? 'Indeed' :
                               app.source === 'referral' ? 'Referral' :
                               app.source === 'campus' ? 'Campus' :
                               app.source === 'job_board' ? 'Job Board' :
                               app.source === 'manual' ? 'Sourced' :
                               app.source.charAt(0).toUpperCase() + app.source.slice(1)}
                            </span>
                          )}
                          {app.manuallyAdded && (
                            <span className="source-badge source-recruiter-added">Recruiter Added</span>
                          )}
                          {app.candidateInfo?.linkedinUrl && (
                            <a
                              href={app.candidateInfo.linkedinUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="source-link-icon"
                              title="View LinkedIn Profile"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="#0A66C2">
                                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                              </svg>
                            </a>
                          )}
                        </div>
                      )}
                      
                      {/* Rating Display */}
                      {(app.ratings?.overall || app.rating) && (
                        <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          <span style={{ fontSize: '0.875rem', color: '#F59E0B' }}>
                            {'★'.repeat(Math.floor(app.ratings?.overall || app.rating || 0))}
                            {'☆'.repeat(5 - Math.floor(app.ratings?.overall || app.rating || 0))}
                          </span>
                          <span style={{ fontSize: '0.75rem', color: '#6B7280' }}>
                            {(app.ratings?.overall || app.rating || 0).toFixed(1)}/5
                          </span>
                        </div>
                      )}
                      
                      {/* Tags Display */}
                      {app.tags && app.tags.length > 0 && (
                        <div style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                          {app.tags.slice(0, 3).map(tag => (
                            <span
                              key={tag}
                              style={{
                                fontSize: '0.6875rem',
                                padding: '0.125rem 0.5rem',
                                background: '#F3F4F6',
                                color: '#374151',
                                borderRadius: '4px',
                                border: '1px solid #D1D5DB'
                              }}
                            >
                              {tag}
                            </span>
                          ))}
                          {app.tags.length > 3 && (
                            <span style={{ fontSize: '0.6875rem', color: '#6B7280' }}>
                              +{app.tags.length - 3} more
                            </span>
                          )}
                        </div>
                      )}
                      
                      {/* Notes indicator */}
                      {app.recruiterNotes && app.recruiterNotes.length > 0 && (
                        <div style={{ marginTop: '0.25rem', fontSize: '0.75rem', color: '#6B7280' }}>
                          {app.recruiterNotes.length} note{app.recruiterNotes.length !== 1 ? 's' : ''}
                        </div>
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {app.resume ? (
                        <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center' }}>
                          <button
                            onClick={() => handlePreviewResume(app._id, app.resume.filename)}
                            style={{
                              background: '#10B981',
                              color: 'white',
                              border: 'none',
                              padding: '0.4rem 0.6rem',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.8rem',
                              fontWeight: '500'
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#059669'}
                            onMouseLeave={(e) => e.target.style.background = '#10B981'}
                            title={`Preview ${app.resume.filename}`}
                          >
                            Preview
                          </button>
                          <button
                            onClick={() => handleDownloadResume(app._id, app.resume.filename)}
                            style={{
                              background: '#4F46E5',
                              color: 'white',
                              border: 'none',
                              padding: '0.4rem 0.6rem',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.8rem',
                              fontWeight: '500'
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#4338CA'}
                            onMouseLeave={(e) => e.target.style.background = '#4F46E5'}
                            title={`Download ${app.resume.filename}`}
                          >
                            Download
                          </button>
                        </div>
                      ) : (
                        <span style={{ color: '#9CA3AF', fontSize: '0.85rem' }}>No resume</span>
                      )}
                    </td>
                    <td style={{ fontSize: '0.9rem' }}>
                      {app.candidateInfo.yearsOfExperience ?
                        `${app.candidateInfo.yearsOfExperience} years` :
                        <span style={{ color: '#9CA3AF' }}>Not specified</span>
                      }
                    </td>
                    <td>
                      {app.assessmentProgress ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                          <div style={{ 
                            fontSize: '0.875rem', 
                            fontWeight: '600',
                            color: '#111827'
                          }}>
                            {app.assessmentProgress.completedLabs}/{app.assessmentProgress.totalLabs} Labs
                          </div>
                          <div style={{
                            background: '#E5E7EB',
                            borderRadius: '4px',
                            height: '6px',
                            width: '80px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              background: app.assessmentProgress.progressPercentage === 100 ? '#10B981' : '#3B82F6',
                              height: '100%',
                              width: `${app.assessmentProgress.progressPercentage}%`,
                              transition: 'width 0.3s ease',
                              borderRadius: '4px'
                            }}></div>
                          </div>
                          <div style={{ 
                            fontSize: '0.75rem', 
                            color: '#6B7280' 
                          }}>
                            {app.assessmentProgress.progressPercentage}%
                          </div>
                        </div>
                      ) : app.assessmentInviteId ? (
                        <span style={{ color: '#9CA3AF', fontSize: '0.85rem' }}>Not started</span>
                      ) : (
                        <span style={{ color: '#9CA3AF', fontSize: '0.85rem' }}>No assessment</span>
                      )}
                    </td>
                    <td style={{ fontSize: '0.9rem' }}>
                      {app.createdAt ?
                        new Date(app.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        }) :
                        <span style={{ color: '#9CA3AF' }}>Unknown</span>
                      }
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <select
                          className={`status-badge ${getStatusBadgeClass(app.status)}`}
                          value={app.status || 'applied'}
                          onChange={(e) => handleUpdateStatus(app._id, e.target.value)}
                          style={{
                            padding: '0.4rem 0.6rem',
                            borderRadius: '6px',
                            border: '1px solid #E5E7EB',
                            fontSize: '0.85rem',
                            fontWeight: '500',
                            cursor: 'pointer',
                            flex: 1
                          }}
                        >
                          <option value="new">New</option>
                          <option value="reviewing">Reviewing</option>
                          <option value="shortlisted">Shortlisted</option>
                          <option value="screening_call">Screening Call</option>
                          <option value="screening_passed">Screening Passed</option>
                          <option value="questionnaire_sent">Questionnaire Sent</option>
                          <option value="questionnaire_completed">Questionnaire Done</option>
                          <option value="interview">Interview</option>
                          <option value="assessment_sent">Assessment Sent</option>
                          <option value="assessment_completed">Assessment Completed</option>
                          <option value="offer">Offer</option>
                          <option value="accepted">Accepted</option>
                          <option value="declined">Declined</option>
                          <option value="hired">Hired</option>
                          <option value="rejected">Rejected</option>
                          <option value="withdrawn">Withdrawn</option>
                        </select>
                        <ProcessStatus application={app} compact={true} />
                      </div>
                    </td>
                    <td style={{ position: 'relative', overflow: 'visible' }}>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', position: 'relative' }}>
                        <button
                          onClick={() => handleViewProfile(app)}
                          style={{
                            background: '#10B981',
                            color: 'white',
                            border: 'none',
                            padding: '0.5rem 1rem',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: '500',
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = '#059669';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = '#10B981';
                          }}
                        >
                          View Profile
                        </button>
                        <div style={{ position: 'relative' }}>
                          <button
                            id={`menu-button-${app._id}`}
                            onClick={() => {
                              setOpenMenuId(openMenuId === app._id ? null : app._id);
                            }}
                            style={{
                              background: '#F3F4F6',
                              border: '1px solid #D1D5DB',
                              padding: '0.5rem 0.7rem',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '1.1rem',
                              lineHeight: '1',
                              zIndex: 1001
                            }}
                            title="More Actions"
                          >
                            ⋮
                          </button>
                          {openMenuId === app._id && (
                            <>
                              <div
                                style={{
                                  position: 'fixed',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  zIndex: 999
                                }}
                                onClick={() => setOpenMenuId(null)}
                              />
                              <div
                                id={`dropdown-menu-${app._id}`}
                                style={{
                                  position: 'fixed',
                                  background: 'white',
                                  border: '1px solid #E5E7EB',
                                  borderRadius: '8px',
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                  minWidth: '200px',
                                  zIndex: 1000,
                                  overflow: 'hidden'
                                }}
                                ref={(el) => {
                                  if (el && openMenuId === app._id) {
                                    const button = document.getElementById(`menu-button-${app._id}`);
                                    if (button) {
                                      const rect = button.getBoundingClientRect();
                                      el.style.top = `${rect.bottom + 4}px`;
                                      el.style.right = `${window.innerWidth - rect.right}px`;
                                    }
                                  }
                                }}
                              >
                                <button
                                  onClick={() => {
                                    setOpenMenuId(null);
                                    handleScheduleInterview(app);
                                  }}
                                  style={{
                                    width: '100%',
                                    padding: '0.75rem 1rem',
                                    border: 'none',
                                    background: 'white',
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                    fontSize: '0.9rem',
                                  }}
                                  onMouseEnter={(e) => e.target.style.background = '#F9FAFB'}
                                  onMouseLeave={(e) => e.target.style.background = 'white'}
                                >
                                  {app.hasInterview ? 'Reschedule Interview' : 'Schedule Interview'}
                                </button>
                                <button
                                  onClick={() => {
                                    setOpenMenuId(null);
                                    handleAssignAssessment(app);
                                  }}
                                  style={{
                                    width: '100%',
                                    padding: '0.75rem 1rem',
                                    border: 'none',
                                    background: 'white',
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                    fontSize: '0.9rem',
                                  }}
                                  onMouseEnter={(e) => e.target.style.background = '#F9FAFB'}
                                  onMouseLeave={(e) => e.target.style.background = 'white'}
                                >
                                  Assign Assessment
                                </button>
                                <button
                                  onClick={() => {
                                    setOpenMenuId(null);
                                    handleSendQuestionnaire(app);
                                  }}
                                  style={{
                                    width: '100%',
                                    padding: '0.75rem 1rem',
                                    border: 'none',
                                    background: 'white',
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                    fontSize: '0.9rem',
                                  }}
                                  onMouseEnter={(e) => e.target.style.background = '#F9FAFB'}
                                  onMouseLeave={(e) => e.target.style.background = 'white'}
                                >
                                  Send Questionnaire
                                </button>
                                {canViewQuestionnaireResponses(app) && (
                                  <button
                                    onClick={() => {
                                      setOpenMenuId(null);
                                      setSelectedApplication(app);
                                      setShowResponseViewer(true);
                                    }}
                                    style={{
                                      width: '100%',
                                      padding: '0.75rem 1rem',
                                      border: 'none',
                                      background: 'white',
                                      textAlign: 'left',
                                      cursor: 'pointer',
                                      fontSize: '0.9rem',
                                      color: '#059669',
                                      fontWeight: '500'
                                    }}
                                    onMouseEnter={(e) => e.target.style.background = '#F0FDF4'}
                                    onMouseLeave={(e) => e.target.style.background = 'white'}
                                  >
                                    View Questionnaire Responses
                                  </button>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* All Modals Below */}
      {showJobEditor && (
        <JobEditor
          jobId={jobId}
          onClose={() => setShowJobEditor(false)}
          onSave={() => {
            setShowJobEditor(false);
            loadApplications();
          }}
        />
      )}
      
      {/* Status Change Confirmation Modal */}
      <ConfirmModal
        isOpen={statusChangeConfirmation.isOpen}
        onClose={() => setStatusChangeConfirmation({
          isOpen: false,
          applicationId: null,
          currentStatus: null,
          newStatus: null,
          applicationName: null,
          force: false
        })}
        onConfirm={confirmStatusChange}
        title={statusChangeConfirmation.force ? "Revert Status Change" : "Confirm Status Change"}
        message={
          statusChangeConfirmation.force
            ? `Are you sure you want to revert the status for ${statusChangeConfirmation.applicationName || 'this candidate'} from "${statusChangeConfirmation.currentStatus}" (terminal state) to "${statusChangeConfirmation.newStatus}"? This will bypass normal validation rules.`
            : statusChangeConfirmation.newStatus && ['rejected', 'withdrawn', 'hired', 'declined'].includes(statusChangeConfirmation.newStatus)
            ? `Are you sure you want to change the status for ${statusChangeConfirmation.applicationName || 'this candidate'} from "${statusChangeConfirmation.currentStatus}" to "${statusChangeConfirmation.newStatus}"? This is a terminal state and cannot be easily reversed without using the revert option.`
            : statusChangeConfirmation.currentStatus && ['rejected', 'withdrawn', 'hired', 'declined'].includes(statusChangeConfirmation.currentStatus)
            ? `Are you sure you want to change the status for ${statusChangeConfirmation.applicationName || 'this candidate'} from "${statusChangeConfirmation.currentStatus}" (terminal state) to "${statusChangeConfirmation.newStatus}"? This will revert the terminal state.`
            : `Are you sure you want to change the status for ${statusChangeConfirmation.applicationName || 'this candidate'} from "${statusChangeConfirmation.currentStatus}" to "${statusChangeConfirmation.newStatus}"?`
        }
        confirmText={statusChangeConfirmation.force ? "Revert Status" : "Confirm Change"}
        cancelText="Cancel"
        variant={statusChangeConfirmation.force || ['rejected', 'withdrawn', 'hired', 'declined'].includes(statusChangeConfirmation.newStatus) ? 'danger' : 'warning'}
      />
      
      {/* Interview Scheduling Modal */}
      {showInterviewModal && (
        <InterviewScheduler
          application={selectedApplication}
          jobId={jobId}
          existingInterview={interviewDetails}
          onClose={() => setShowInterviewModal(false)}
          onScheduled={async () => {
            // Status is automatically updated to 'interview' by backend
            toastr.success('Interview scheduled successfully');
            setShowInterviewModal(false);
            loadApplications();
            // Refresh interview details for the selected application
            if (selectedApplication && selectedApplication._id) {
              await loadInterviewDetails(selectedApplication._id);
            }
          }}
        />
      )}

      {/* Assessment Assignment Modal */}
      {showAssessmentModal && (
        <AssessmentAssigner
          application={selectedApplication}
          jobId={jobId}
          onClose={() => setShowAssessmentModal(false)}
          onAssigned={async () => {
            // Status is automatically updated to 'assessment_sent' by backend
            toastr.success('Assessment assigned successfully');
            setShowAssessmentModal(false);
            loadApplications();
          }}
        />
      )}

      {/* Send Questionnaire Modal - supports single and bulk, with extra questions step */}
      {showQuestionnaireModal && (selectedApplication || bulkQuestionnaireTargets.length > 0) && (
        <Drawer
        isOpen={true}
        onClose={() => {
          setShowQuestionnaireModal(false);
          setQuestionnaires([]);
          setBulkQuestionnaireTargets([]);
          setSelectedQuestionnaireForSend(null);
          setExtraQuestions([]);
        }}
        title={
          selectedQuestionnaireForSend
            ? 'Customize & Send Questionnaire'
            : bulkQuestionnaireTargets.length > 0
              ? `Send Questionnaire to ${bulkQuestionnaireTargets.length} Candidates`
              : 'Send Pre-Interview Questionnaire'
        }
        width="lg"
        footer={
          selectedQuestionnaireForSend ? (
            <div style={{ display: 'flex', gap: '0.75rem', width: '100%' }}>
              <button
                type="button"
                className="drawer-btn drawer-btn-secondary"
                onClick={() => {
                  setSelectedQuestionnaireForSend(null);
                  setExtraQuestions([]);
                }}
              >
                Back
              </button>
              <button
                type="button"
                className="drawer-btn drawer-btn-primary"
                disabled={sendingQuestionnaire}
                onClick={async () => {
                  const targetIds = bulkQuestionnaireTargets.length > 0
                    ? bulkQuestionnaireTargets
                    : [selectedApplication._id];
                  const validExtra = extraQuestions.filter(q => q.question.trim());
                  setSendingQuestionnaire(true);
                  try {
                    await questionnairesAPI.send(selectedQuestionnaireForSend._id, targetIds, validExtra.length > 0 ? validExtra : undefined);
                    const count = targetIds.length;
                    toastr.success(`Questionnaire sent to ${count} candidate${count !== 1 ? 's' : ''}!`);
                    setShowQuestionnaireModal(false);
                    setQuestionnaires([]);
                    setBulkQuestionnaireTargets([]);
                    setSelectedApplicationIds([]);
                    setSelectedQuestionnaireForSend(null);
                    setExtraQuestions([]);
                    loadApplications();
                  } catch (error) {
                    console.error('Failed to send questionnaire:', error);
                    toastr.error(error.response?.data?.error || 'Failed to send questionnaire');
                  } finally {
                    setSendingQuestionnaire(false);
                  }
                }}
                style={{ flex: 1 }}
              >
                {sendingQuestionnaire ? 'Sending...' : `Send Questionnaire${extraQuestions.filter(q => q.question.trim()).length > 0 ? ` (+${extraQuestions.filter(q => q.question.trim()).length} custom)` : ''}`}
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="drawer-btn drawer-btn-secondary"
              onClick={() => {
                setShowQuestionnaireModal(false);
                setQuestionnaires([]);
                setBulkQuestionnaireTargets([]);
              }}
            >
              Close
            </button>
          )
        }
      >
        {selectedQuestionnaireForSend ? (
          /* Step 2: Customize before sending */
          <div>
            <div style={{
              background: '#F0F9FF',
              border: '1px solid #BAE6FD',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1.5rem'
            }}>
              <div style={{ fontSize: '0.9375rem', fontWeight: '600', color: '#0369A1', marginBottom: '0.25rem' }}>
                {selectedQuestionnaireForSend.title}
              </div>
              <div style={{ fontSize: '0.8125rem', color: '#0284C7' }}>
                {selectedQuestionnaireForSend.questions.length} template question{selectedQuestionnaireForSend.questions.length !== 1 ? 's' : ''}
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.8125rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Template Questions
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                {selectedQuestionnaireForSend.questions.map((q, i) => (
                  <div key={q._id || i} style={{
                    padding: '0.5rem 0.75rem',
                    background: '#F9FAFB',
                    borderRadius: '6px',
                    border: '1px solid #E5E7EB',
                    fontSize: '0.8125rem',
                    color: '#374151',
                    display: 'flex',
                    gap: '0.5rem'
                  }}>
                    <span style={{ color: '#9CA3AF', fontWeight: '600', flexShrink: 0 }}>{i + 1}.</span>
                    <span>{q.question}</span>
                    <span style={{ marginLeft: 'auto', color: '#9CA3AF', fontSize: '0.75rem', flexShrink: 0 }}>{q.type}</span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: '1rem', marginTop: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                <div>
                  <div style={{ fontSize: '0.8125rem', fontWeight: '600', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Extra Questions for This Candidate
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6B7280', marginTop: '0.125rem' }}>
                    These are added only for this profile, not to the template
                  </div>
                </div>
                <button
                  onClick={() => setExtraQuestions([...extraQuestions, { question: '', type: 'textarea', required: false }])}
                  style={{
                    padding: '0.375rem 0.75rem',
                    background: '#EEF2FF',
                    color: '#4F46E5',
                    border: '1px solid #C7D2FE',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '500',
                    fontSize: '0.8125rem',
                    whiteSpace: 'nowrap'
                  }}
                >
                  + Add Question
                </button>
              </div>

              {extraQuestions.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '1.5rem',
                  border: '1px dashed #D1D5DB',
                  borderRadius: '8px',
                  color: '#9CA3AF',
                  fontSize: '0.8125rem'
                }}>
                  No extra questions. Click "Add Question" to append custom questions for this candidate.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {extraQuestions.map((eq, idx) => (
                    <div key={idx} style={{
                      padding: '0.75rem',
                      background: '#FFFBEB',
                      border: '1px solid #FDE68A',
                      borderRadius: '8px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <span style={{ color: '#D97706', fontWeight: '600', fontSize: '0.8125rem', flexShrink: 0, marginTop: '0.375rem' }}>
                          Q{selectedQuestionnaireForSend.questions.length + idx + 1}.
                        </span>
                        <input
                          type="text"
                          value={eq.question}
                          onChange={(e) => {
                            const updated = [...extraQuestions];
                            updated[idx] = { ...updated[idx], question: e.target.value };
                            setExtraQuestions(updated);
                          }}
                          placeholder="Type your question here..."
                          style={{
                            flex: 1,
                            padding: '0.375rem 0.5rem',
                            border: '1px solid #FDE68A',
                            borderRadius: '4px',
                            fontSize: '0.8125rem',
                            outline: 'none',
                            background: '#FFFFFF'
                          }}
                        />
                        <button
                          onClick={() => setExtraQuestions(extraQuestions.filter((_, i) => i !== idx))}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#EF4444',
                            cursor: 'pointer',
                            fontSize: '1.125rem',
                            padding: '0.25rem',
                            lineHeight: 1,
                            flexShrink: 0
                          }}
                          title="Remove question"
                        >
                          &times;
                        </button>
                      </div>
                      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginLeft: '1.75rem' }}>
                        <select
                          value={eq.type}
                          onChange={(e) => {
                            const updated = [...extraQuestions];
                            updated[idx] = { ...updated[idx], type: e.target.value };
                            setExtraQuestions(updated);
                          }}
                          style={{
                            padding: '0.25rem 0.5rem',
                            border: '1px solid #FDE68A',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            background: '#FFFFFF',
                            color: '#92400E'
                          }}
                        >
                          <option value="textarea">Long Text</option>
                          <option value="text">Short Text</option>
                          <option value="yes_no">Yes / No</option>
                          <option value="scale">Scale (1-10)</option>
                        </select>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.75rem', color: '#92400E', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={eq.required}
                            onChange={(e) => {
                              const updated = [...extraQuestions];
                              updated[idx] = { ...updated[idx], required: e.target.checked };
                              setExtraQuestions(updated);
                            }}
                          />
                          Required
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : loadingQuestionnaires ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#6B7280' }}>
            Loading questionnaires...
          </div>
        ) : questionnaires.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p style={{ color: '#6B7280', marginBottom: '1rem' }}>
              No active questionnaires found for this job posting.
            </p>
            <button
              onClick={() => navigate(`/recruiter/jobs/${jobId}/questionnaires/create`)}
              style={{
                padding: '0.625rem 1.25rem',
                background: '#1E40AF',
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '500',
                fontSize: '0.875rem'
              }}
              onMouseEnter={(e) => e.target.style.background = '#1E3A8A'}
              onMouseLeave={(e) => e.target.style.background = '#1E40AF'}
            >
              Create Questionnaire
            </button>
          </div>
        ) : (
          /* Step 1: Select a questionnaire */
          <div>
            {bulkQuestionnaireTargets.length > 0 ? (
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{
                  background: '#EEF2FF',
                  border: '1px solid #C7D2FE',
                  borderRadius: '8px',
                  padding: '0.75rem 1rem',
                  marginBottom: '1rem'
                }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#4338CA', marginBottom: '0.5rem' }}>
                    Sending to {bulkQuestionnaireTargets.length} candidate{bulkQuestionnaireTargets.length !== 1 ? 's' : ''}:
                  </div>
                  <div style={{ fontSize: '0.8125rem', color: '#6366F1', maxHeight: '120px', overflowY: 'auto' }}>
                    {applications.filter(a => bulkQuestionnaireTargets.includes(a._id)).map((a, i) => (
                      <div key={a._id} style={{ padding: '2px 0' }}>
                        {i + 1}. {a.candidateInfo.firstName} {a.candidateInfo.lastName} ({a.candidateInfo.email})
                      </div>
                    ))}
                  </div>
                </div>
                <p style={{ color: '#6B7280', fontSize: '0.875rem' }}>
                  Select a questionnaire to send to all selected candidates:
                </p>
              </div>
            ) : (
              <p style={{ color: '#6B7280', marginBottom: '1.5rem', fontSize: '0.875rem' }}>
                Select a questionnaire to send to <strong>{selectedApplication?.candidateInfo?.firstName} {selectedApplication?.candidateInfo?.lastName}</strong>
              </p>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {questionnaires.map((q) => (
                <button
                  key={q._id}
                  onClick={() => {
                    setSelectedQuestionnaireForSend(q);
                    setExtraQuestions([]);
                  }}
                  style={{
                    padding: '1rem',
                    background: '#FFFFFF',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontSize: '0.9375rem',
                    color: '#374151',
                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#8B5CF6';
                    e.currentTarget.style.background = '#F5F3FF';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#D1D5DB';
                    e.currentTarget.style.background = '#FFFFFF';
                  }}
                >
                  <div style={{ fontWeight: '600', marginBottom: '0.25rem', color: '#111827', pointerEvents: 'none' }}>
                    {q.title}
                  </div>
                  {q.description && (
                    <div style={{ fontSize: '0.875rem', color: '#6B7280', marginBottom: '0.5rem', pointerEvents: 'none' }}>
                      {q.description}
                    </div>
                  )}
                  <div style={{ fontSize: '0.75rem', color: '#9CA3AF', pointerEvents: 'none' }}>
                    {q.questions.length} question{q.questions.length !== 1 ? 's' : ''} • Deadline: {q.deadline} hours
                  </div>
                </button>
              ))}
            </div>
            <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid #E5E7EB' }}>
              <button
                onClick={() => navigate(`/recruiter/jobs/${jobId}/questionnaires/create`)}
                style={{
                  width: '100%',
                  padding: '0.625rem 1.25rem',
                  background: 'transparent',
                  color: '#4F46E5',
                  border: '1px dashed #C7D2FE',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '0.875rem'
                }}
                onMouseEnter={(e) => e.target.style.background = '#EEF2FF'}
                onMouseLeave={(e) => e.target.style.background = 'transparent'}
              >
                + Create New Questionnaire
              </button>
            </div>
          </div>
        )}
      </Drawer>
      )}

      {/* Questionnaire Response Viewer */}
      {showResponseViewer && selectedApplication && (
        <Drawer
        isOpen={true}
        onClose={() => {
          setShowResponseViewer(false);
        }}
        title="Questionnaire Responses"
        width="lg"
      >
        <QuestionnaireResponseViewer
          applicationId={selectedApplication._id}
          onClose={() => setShowResponseViewer(false)}
        />
      </Drawer>
      )}

      {/* Phone Screening Scheduler */}
      {showPhoneScreeningScheduler && selectedApplication && (
        <PhoneScreeningScheduler
        application={selectedApplication}
        onClose={() => {
          setShowPhoneScreeningScheduler(false);
        }}
        onScheduled={() => {
          setShowPhoneScreeningScheduler(false);
          loadApplications();
        }}
        />
      )}

      {/* Phone Screening Conduct */}
      {showPhoneScreeningConduct && phoneScreening && selectedApplication && (
        <PhoneScreeningConduct
        screening={phoneScreening}
        onClose={() => {
          setShowPhoneScreeningConduct(false);
          setPhoneScreening(null);
        }}
        onCompleted={() => {
          setShowPhoneScreeningConduct(false);
          setPhoneScreening(null);
          loadApplications();
        }}
        />
      )}

      {/* Approval Request Modal */}
      {showApprovalRequestModal && selectedApplication && (
        <ApprovalRequestModal
        application={selectedApplication}
        jobId={jobId}
        onClose={() => {
          setShowApprovalRequestModal(false);
        }}
        onRequested={() => {
          setShowApprovalRequestModal(false);
          loadApplications();
        }}
        />
      )}

      {/* Interviewer Availability Manager Drawer */}
      {showAvailabilityManager && selectedApplication && (
        <Drawer
          isOpen={showAvailabilityManager}
          onClose={() => setShowAvailabilityManager(false)}
          title="Interview Availability Coordination"
          width="xl"
          customWidth="900px"
        >
          <InterviewerAvailabilityManager
            applicationId={selectedApplication._id}
            jobId={jobId}
            candidateEmail={selectedApplication.candidateInfo.email}
            candidateName={`${selectedApplication.candidateInfo.firstName} ${selectedApplication.candidateInfo.lastName}`}
            onClose={() => setShowAvailabilityManager(false)}
            hideHeader={true}
            onAvailabilityRequested={(request) => {
              toastr.success('Availability request sent to candidate');
              setShowAvailabilityManager(false);
              loadApplications();
            }}
          />
        </Drawer>
      )}

      {/* Group Interview Scheduling Modal */}
      {showGroupInterviewModal && (
        <GroupInterviewScheduler
          selectedApplications={applications.filter(app => selectedApplicationIds.includes(app._id))}
          jobId={jobId}
          onClose={() => setShowGroupInterviewModal(false)}
          onScheduled={() => {
            setShowGroupInterviewModal(false);
            setSelectedApplicationIds([]);
            loadApplications();
          }}
        />
      )}

      {/* Add Candidate Drawer */}
      {showAddCandidateModal && (
        <Drawer
          isOpen={showAddCandidateModal}
          onClose={() => setShowAddCandidateModal(false)}
          title="Add Candidate"
          width="md"
        >
          <AddCandidateDrawerContent
            jobId={jobId}
            jobTitle={job?.title || ''}
            onClose={() => setShowAddCandidateModal(false)}
            onSuccess={(newApplication) => {
              setApplications(prev => [newApplication, ...prev]);
              loadApplications();
            }}
          />
        </Drawer>
      )}

      {/* Candidate Profile Drawer */}
      {showProfileModal && selectedApplication && (
          <Drawer
            key={`profile-${selectedApplication._id}`}
            isOpen={showProfileModal}
            onClose={() => {
              setShowProfileModal(false);
              setActiveProfileTab('overview');
              setShowActionsMenu(false);
              setEditingProfile(false);
            }}
            title="Candidate Profile"
            width="xl"
            customWidth="900px"
            drawerContentRef={drawerContentRef}
            footer={null}
          >
            {/* Candidate Header Section */}
            <div style={{ 
              marginBottom: '1.5rem', 
              paddingBottom: '1.25rem',
              borderBottom: '2px solid #E5E7EB'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                <div>
                  <h2 style={{ 
                    fontSize: '1.75rem', 
                    marginBottom: '0.5rem', 
                    color: '#111827', 
                    fontWeight: '700',
                    letterSpacing: '-0.025em',
                    lineHeight: '1.2'
                  }}>
                    {selectedApplication.candidateInfo.firstName} {selectedApplication.candidateInfo.lastName}
                  </h2>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <div style={{ 
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '0.375rem 0.875rem',
                      background: '#F3F4F6',
                      borderRadius: '6px',
                      color: '#374151',
                      textTransform: 'capitalize',
                      fontSize: '0.8125rem',
                      fontWeight: '600',
                      letterSpacing: '0.05em'
                    }}>
                      {selectedApplication.status.replace(/_/g, ' ')}
                    </div>
                    {selectedApplication.source && selectedApplication.source !== 'direct' && (
                      <span className={`source-badge source-${selectedApplication.source}`} style={{ fontSize: '0.75rem' }}>
                        {selectedApplication.source === 'linkedin' ? 'LinkedIn' :
                         selectedApplication.source === 'naukri' ? 'Naukri' :
                         selectedApplication.source === 'indeed' ? 'Indeed' :
                         selectedApplication.source === 'referral' ? 'Referral' :
                         selectedApplication.source === 'campus' ? 'Campus' :
                         selectedApplication.source === 'job_board' ? 'Job Board' :
                         selectedApplication.source === 'manual' ? 'Sourced' :
                         selectedApplication.source.charAt(0).toUpperCase() + selectedApplication.source.slice(1)}
                      </span>
                    )}
                    {selectedApplication.manuallyAdded && (
                      <span className="source-badge source-recruiter-added" style={{ fontSize: '0.75rem' }}>Recruiter Added</span>
                    )}
                  </div>
                </div>
                {/* Quick Actions - Always Visible */}
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <button
                    onClick={() => {
                      if (editingProfile) {
                        setEditingProfile(false);
                      } else {
                        const ci = selectedApplication.candidateInfo || {};
                        setEditForm({
                          firstName: ci.firstName || '',
                          lastName: ci.lastName || '',
                          email: ci.email || '',
                          phone: ci.phone || '',
                          location: ci.location || '',
                          linkedinUrl: ci.linkedinUrl || '',
                          naukriUrl: ci.naukriUrl || '',
                          githubUrl: ci.githubUrl || '',
                          portfolioUrl: ci.portfolioUrl || '',
                          currentCompany: ci.currentCompany || '',
                          currentTitle: ci.currentTitle || '',
                          experienceYears: ci.experienceYears || '',
                          noticePeriod: ci.noticePeriod || '',
                          currentCTC: ci.currentCTC || '',
                          expectedCTC: ci.expectedCTC || '',
                        });
                        setEditingProfile(true);
                      }
                    }}
                    style={{
                      padding: '0.625rem 1rem',
                      background: editingProfile ? '#6B7280' : '#F3F4F6',
                      color: editingProfile ? '#fff' : '#374151',
                      border: '1px solid #D1D5DB',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '0.8125rem',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {editingProfile ? 'Cancel Edit' : 'Edit Profile'}
                  </button>
                  <button
                    onClick={() => {
                      handleScheduleInterview(selectedApplication);
                    }}
                    style={{
                      padding: '0.625rem 1rem',
                      background: hasExistingInterview ? '#F59E0B' : '#3B82F6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '0.8125rem',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => e.target.style.background = hasExistingInterview ? '#D97706' : '#2563EB'}
                    onMouseLeave={(e) => e.target.style.background = hasExistingInterview ? '#F59E0B' : '#3B82F6'}
                  >
                    {hasExistingInterview ? 'Reschedule' : 'Schedule'}
                  </button>
                  <button
                    onClick={() => {
                      handleAssignAssessment(selectedApplication);
                    }}
                    style={{
                      padding: '0.625rem 1rem',
                      background: '#3B82F6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '0.8125rem',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => e.target.style.background = '#2563EB'}
                    onMouseLeave={(e) => e.target.style.background = '#3B82F6'}
                  >
                    Assessment
                  </button>
                  <div style={{ position: 'relative' }}>
                    <button
                      onClick={() => setShowActionsMenu(!showActionsMenu)}
                      style={{
                        padding: '0.625rem 1rem',
                        background: '#6B7280',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '0.8125rem',
                        whiteSpace: 'nowrap',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.375rem'
                      }}
                      onMouseEnter={(e) => e.target.style.background = '#4B5563'}
                      onMouseLeave={(e) => e.target.style.background = '#6B7280'}
                    >
                      More
                      <span style={{ fontSize: '0.75rem' }}>▼</span>
                    </button>
                    {showActionsMenu && (
                      <>
                        <div 
                          style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 998
                          }}
                          onClick={() => setShowActionsMenu(false)}
                        />
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          right: 0,
                          marginTop: '0.5rem',
                          background: 'white',
                          border: '1px solid #E5E7EB',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          minWidth: '220px',
                          zIndex: 999,
                          overflow: 'hidden'
                        }}>
                          {interviewDetails && (
                            <button
                              onClick={() => {
                                setShowActionsMenu(false);
                                handleSendReminder();
                              }}
                              disabled={sendingReminder}
                              style={{
                                width: '100%',
                                padding: '0.75rem 1rem',
                                background: 'white',
                                border: 'none',
                                borderBottom: '1px solid #F3F4F6',
                                textAlign: 'left',
                                cursor: sendingReminder ? 'not-allowed' : 'pointer',
                                fontSize: '0.875rem',
                                color: '#374151',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem',
                                opacity: sendingReminder ? 0.5 : 1
                              }}
                              onMouseEnter={(e) => !sendingReminder && (e.target.style.background = '#F9FAFB')}
                              onMouseLeave={(e) => !sendingReminder && (e.target.style.background = 'white')}
                            >
                              <span style={{ fontSize: '0.875rem', width: '20px', textAlign: 'center', color: '#6B7280', fontFamily: 'monospace' }}>•</span>
                              <span>{sendingReminder ? 'Sending...' : 'Send Reminder'}</span>
                            </button>
                          )}
                          <button
                            onClick={() => {
                              setShowActionsMenu(false);
                              handleSchedulePhoneScreening(selectedApplication);
                            }}
                            style={{
                              width: '100%',
                              padding: '0.75rem 1rem',
                              background: 'white',
                              border: 'none',
                              borderBottom: '1px solid #F3F4F6',
                              textAlign: 'left',
                              cursor: 'pointer',
                              fontSize: '0.875rem',
                              color: '#374151',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.75rem'
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#F9FAFB'}
                            onMouseLeave={(e) => e.target.style.background = 'white'}
                          >
                            <span style={{ fontSize: '0.875rem', width: '20px', textAlign: 'center', color: '#6B7280', fontFamily: 'monospace' }}>•</span>
                            <span>Schedule Phone Screening</span>
                          </button>
                          <button
                            onClick={() => {
                              setShowActionsMenu(false);
                              handleConductPhoneScreening(selectedApplication);
                            }}
                            style={{
                              width: '100%',
                              padding: '0.75rem 1rem',
                              background: 'white',
                              border: 'none',
                              borderBottom: '1px solid #F3F4F6',
                              textAlign: 'left',
                              cursor: 'pointer',
                              fontSize: '0.875rem',
                              color: '#374151',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.75rem'
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#F9FAFB'}
                            onMouseLeave={(e) => e.target.style.background = 'white'}
                          >
                            <span style={{ fontSize: '0.875rem', width: '20px', textAlign: 'center', color: '#6B7280', fontFamily: 'monospace' }}>•</span>
                            <span>Conduct Screening Call</span>
                          </button>
                          <button
                            onClick={() => {
                              setShowActionsMenu(false);
                              setShowAvailabilityManager(true);
                            }}
                            style={{
                              width: '100%',
                              padding: '0.75rem 1rem',
                              background: 'white',
                              border: 'none',
                              borderBottom: '1px solid #F3F4F6',
                              textAlign: 'left',
                              cursor: 'pointer',
                              fontSize: '0.875rem',
                              color: '#374151',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.75rem'
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#F9FAFB'}
                            onMouseLeave={(e) => e.target.style.background = 'white'}
                          >
                            <span style={{ fontSize: '0.875rem', width: '20px', textAlign: 'center', color: '#6B7280', fontFamily: 'monospace' }}>•</span>
                            <span>Request Availability</span>
                          </button>
                          <button
                            onClick={() => {
                              setShowActionsMenu(false);
                              setShowApprovalRequestModal(true);
                            }}
                            style={{
                              width: '100%',
                              padding: '0.75rem 1rem',
                              background: 'white',
                              border: 'none',
                              textAlign: 'left',
                              cursor: 'pointer',
                              fontSize: '0.875rem',
                              color: '#374151',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.75rem'
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#F9FAFB'}
                            onMouseLeave={(e) => e.target.style.background = 'white'}
                          >
                            <span style={{ fontSize: '0.875rem', width: '20px', textAlign: 'center', color: '#6B7280', fontFamily: 'monospace' }}>•</span>
                            <span>Request Approval</span>
                          </button>
                          <button
                            onClick={() => {
                              setShowActionsMenu(false);
                              handleSendQuestionnaire(selectedApplication);
                            }}
                            style={{
                              width: '100%',
                              padding: '0.75rem 1rem',
                              background: 'white',
                              border: 'none',
                              borderBottom: '1px solid #F3F4F6',
                              textAlign: 'left',
                              cursor: 'pointer',
                              fontSize: '0.875rem',
                              color: '#374151',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.75rem'
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#F9FAFB'}
                            onMouseLeave={(e) => e.target.style.background = 'white'}
                          >
                            <span style={{ fontSize: '0.875rem', width: '20px', textAlign: 'center', color: '#6B7280', fontFamily: 'monospace' }}>•</span>
                            <span>Send Questionnaire</span>
                          </button>
                          {canViewQuestionnaireResponses(selectedApplication) && (
                          <button
                            onClick={() => {
                              setShowActionsMenu(false);
                              setShowResponseViewer(true);
                            }}
                            style={{
                              width: '100%',
                              padding: '0.75rem 1rem',
                              background: 'white',
                              border: 'none',
                              borderBottom: '1px solid #F3F4F6',
                              textAlign: 'left',
                              cursor: 'pointer',
                              fontSize: '0.875rem',
                              color: '#059669',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.75rem',
                              fontWeight: '500'
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#F0FDF4'}
                            onMouseLeave={(e) => e.target.style.background = 'white'}
                          >
                            <span style={{ fontSize: '0.875rem', width: '20px', textAlign: 'center', color: '#059669', fontFamily: 'monospace' }}>✓</span>
                            <span>View Questionnaire Responses</span>
                          </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Tab Navigation */}
            <div style={{ 
              display: 'flex', 
              gap: '0.5rem',
              marginBottom: '1.5rem',
              borderBottom: '1px solid #E5E7EB',
              paddingBottom: '0'
            }}>
              <button
                onClick={() => setActiveProfileTab('overview')}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '0.75rem 1.25rem',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: activeProfileTab === 'overview' ? '#3B82F6' : '#6B7280',
                  cursor: 'pointer',
                  borderBottom: activeProfileTab === 'overview' ? '2px solid #3B82F6' : '2px solid transparent',
                  marginBottom: '-1px'
                }}
                onMouseEnter={(e) => {
                  if (activeProfileTab !== 'overview') {
                    e.target.style.color = '#374151';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeProfileTab !== 'overview') {
                    e.target.style.color = '#6B7280';
                  }
                }}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveProfileTab('activityLogs')}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '0.75rem 1.25rem',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: activeProfileTab === 'activityLogs' ? '#3B82F6' : '#6B7280',
                  cursor: 'pointer',
                  borderBottom: activeProfileTab === 'activityLogs' ? '2px solid #3B82F6' : '2px solid transparent',
                  marginBottom: '-1px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseEnter={(e) => {
                  if (activeProfileTab !== 'activityLogs') {
                    e.target.style.color = '#374151';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeProfileTab !== 'activityLogs') {
                    e.target.style.color = '#6B7280';
                  }
                }}
              >
                Activity Logs
                {selectedApplication.timeline && selectedApplication.timeline.length > 0 && (
                  <span style={{
                    background: '#3B82F6',
                    color: 'white',
                    borderRadius: '12px',
                    padding: '0.125rem 0.5rem',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    minWidth: '20px',
                    textAlign: 'center'
                  }}>
                    {selectedApplication.timeline.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveProfileTab('evaluation')}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '0.75rem 1.25rem',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: activeProfileTab === 'evaluation' ? '#3B82F6' : '#6B7280',
                  cursor: 'pointer',
                  borderBottom: activeProfileTab === 'evaluation' ? '2px solid #3B82F6' : '2px solid transparent',
                  marginBottom: '-1px'
                }}
                onMouseEnter={(e) => {
                  if (activeProfileTab !== 'evaluation') {
                    e.target.style.color = '#374151';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeProfileTab !== 'evaluation') {
                    e.target.style.color = '#6B7280';
                  }
                }}
              >
                Evaluation
              </button>
            </div>

            {/* Tab Content */}
            {activeProfileTab === 'overview' && (
              <>

            {/* Edit Profile Form */}
            {editingProfile ? (
              <div style={{ marginBottom: '2rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: '700', color: '#111827', margin: 0 }}>Edit Candidate Profile</h3>
                  <button
                    disabled={savingProfile}
                    onClick={async () => {
                      setSavingProfile(true);
                      try {
                        const result = await jobsAPI.updateCandidateInfo(jobId, selectedApplication._id, editForm);
                        const updatedApp = result.data.application;
                        setApplications(prev => prev.map(a => a._id === updatedApp._id ? updatedApp : a));
                        setSelectedApplication(updatedApp);
                        setEditingProfile(false);
                        toastr.success('Profile updated');
                      } catch (err) {
                        toastr.error(err.response?.data?.error || 'Failed to update');
                      } finally {
                        setSavingProfile(false);
                      }
                    }}
                    style={{
                      padding: '0.5rem 1.25rem',
                      background: '#10B981',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: savingProfile ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      fontSize: '0.8125rem',
                      opacity: savingProfile ? 0.7 : 1
                    }}
                  >
                    {savingProfile ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
                {[
                  { label: 'Personal Information', fields: [
                    { key: 'firstName', label: 'First Name', placeholder: 'First name' },
                    { key: 'lastName', label: 'Last Name', placeholder: 'Last name' },
                    { key: 'email', label: 'Email Address', placeholder: 'candidate@example.com', type: 'email',
                      hint: selectedApplication?.emailVerified ? 'Changing this will reset email verification' : null },
                    { key: 'phone', label: 'Phone', placeholder: '+91...' },
                    { key: 'location', label: 'Location', placeholder: 'City, Country' },
                  ]},
                  { label: 'Professional Details', fields: [
                    { key: 'currentCompany', label: 'Company', placeholder: 'Current company' },
                    { key: 'currentTitle', label: 'Job Title', placeholder: 'Current title' },
                    { key: 'experienceYears', label: 'Experience (years)', placeholder: 'e.g. 5' },
                    { key: 'noticePeriod', label: 'Notice Period', type: 'select', options: ['', 'Immediate', '15 Days', '30 Days', '45 Days', '60 Days', '90 Days', 'More than 90 Days'] },
                    { key: 'currentCTC', label: 'Current CTC', placeholder: 'e.g. 8 LPA' },
                    { key: 'expectedCTC', label: 'Expected CTC', placeholder: 'e.g. 12 LPA' },
                  ]},
                  { label: 'Profile Links', fields: [
                    { key: 'linkedinUrl', label: 'LinkedIn', placeholder: 'https://linkedin.com/in/...' },
                    { key: 'githubUrl', label: 'GitHub', placeholder: 'https://github.com/...' },
                    { key: 'portfolioUrl', label: 'Portfolio', placeholder: 'https://...' },
                    { key: 'naukriUrl', label: 'Naukri', placeholder: 'https://naukri.com/...' },
                  ]},
                ].map(section => (
                  <div key={section.label} style={{ marginBottom: '1.25rem' }}>
                    <div style={{ fontSize: '0.6875rem', fontWeight: '700', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
                      {section.label}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                      {section.fields.map(f => (
                        <div key={f.key}>
                          <label style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '500', display: 'block', marginBottom: '0.25rem' }}>{f.label}</label>
                          {f.type === 'select' ? (
                            <select
                              value={editForm[f.key] || ''}
                              onChange={(e) => setEditForm({ ...editForm, [f.key]: e.target.value })}
                              style={{ width: '100%', padding: '0.375rem 0.5rem', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '0.8125rem', background: '#fff' }}
                            >
                              {f.options.map(o => <option key={o} value={o}>{o || 'Select...'}</option>)}
                            </select>
                          ) : (
                            <input
                              type={f.type === 'email' ? 'email' : 'text'}
                              value={editForm[f.key] || ''}
                              onChange={(e) => setEditForm({ ...editForm, [f.key]: e.target.value })}
                              placeholder={f.placeholder}
                              style={{ width: '100%', padding: '0.375rem 0.5rem', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '0.8125rem', boxSizing: 'border-box' }}
                            />
                          )}
                          {f.hint && editForm[f.key] !== (selectedApplication?.candidateInfo?.email || '') && (
                            <span style={{ fontSize: '0.6875rem', color: '#D97706', marginTop: '0.125rem', display: 'block' }}>{f.hint}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
            /* Read-only view */
            <>
            {/* Two Column Layout for Better Space Utilization */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr',
              gap: '2.5rem',
              marginBottom: '2.5rem'
            }}>
              {/* Left Column - Contact Information */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.75rem' }}>
                <div>
                  <label style={{ 
                    fontWeight: '600', 
                    color: '#6B7280', 
                    display: 'block', 
                    marginBottom: '0.625rem', 
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Email Address
                  </label>
                  <div style={{ 
                    color: '#111827', 
                    fontSize: '0.9375rem',
                    fontWeight: '500',
                    wordBreak: 'break-word'
                  }}>
                    {selectedApplication.candidateInfo.email}
                  </div>
                </div>

                <div>
                  <label style={{ 
                    fontWeight: '600', 
                    color: '#6B7280', 
                    display: 'block', 
                    marginBottom: '0.625rem', 
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Phone Number
                  </label>
                  <div style={{ 
                    color: '#111827', 
                    fontSize: '0.9375rem',
                    fontWeight: '500'
                  }}>
                    {selectedApplication.candidateInfo.phone || <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>Not provided</span>}
                  </div>
                </div>

                <div>
                    <label style={{
                      fontWeight: '600',
                      color: '#6B7280',
                      display: 'block',
                      marginBottom: '0.75rem',
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Resume
                    </label>
                    {selectedApplication.resume ? (
                    <>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => handlePreviewResume(selectedApplication._id, selectedApplication.resume.filename)}
                        style={{
                          background: '#10B981',
                          color: 'white',
                          border: 'none',
                          padding: '0.625rem 1.25rem',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          flex: 1
                        }}
                        onMouseEnter={(e) => e.target.style.background = '#059669'}
                        onMouseLeave={(e) => e.target.style.background = '#10B981'}
                      >
                        Preview
                      </button>
                      <button
                        onClick={() => handleDownloadResume(selectedApplication._id, selectedApplication.resume.filename)}
                        style={{
                          background: '#3B82F6',
                          color: 'white',
                          border: 'none',
                          padding: '0.625rem 1.25rem',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          flex: 1
                        }}
                        onMouseEnter={(e) => e.target.style.background = '#2563EB'}
                        onMouseLeave={(e) => e.target.style.background = '#3B82F6'}
                      >
                        Download
                      </button>
                      {/* Replace resume */}
                      <label style={{
                        background: '#F3F4F6',
                        color: '#374151',
                        border: '1px solid #D1D5DB',
                        padding: '0.625rem 1rem',
                        borderRadius: '6px',
                        cursor: resumeUploading ? 'not-allowed' : 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        flex: 1,
                        textAlign: 'center',
                        opacity: resumeUploading ? 0.6 : 1
                      }}>
                        {resumeUploading ? 'Uploading...' : 'Replace'}
                        <input type="file" accept=".pdf,.doc,.docx" style={{ display: 'none' }}
                          disabled={resumeUploading}
                          onChange={(e) => e.target.files[0] && handleResumeUpload(selectedApplication._id, e.target.files[0])} />
                      </label>
                    </div>
                    <div style={{ color: '#9CA3AF', fontSize: '0.75rem', marginTop: '0.375rem' }}>
                      {selectedApplication.resume.filename}
                    </div>
                    </>
                  ) : (
                    /* No resume — show upload */
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      border: '2px dashed #D1D5DB',
                      borderRadius: '8px',
                      padding: '1rem',
                      cursor: resumeUploading ? 'not-allowed' : 'pointer',
                      color: resumeUploading ? '#9CA3AF' : '#6B7280',
                      fontSize: '0.875rem',
                      background: '#F9FAFB',
                      opacity: resumeUploading ? 0.6 : 1
                    }}>
                      {resumeUploading ? 'Uploading...' : 'Upload Resume (PDF, DOC, DOCX)'}
                      <input type="file" accept=".pdf,.doc,.docx" style={{ display: 'none' }}
                        disabled={resumeUploading}
                        onChange={(e) => e.target.files[0] && handleResumeUpload(selectedApplication._id, e.target.files[0])} />
                    </label>
                  )}
                </div>

              </div>

              {/* Right Column - Application Details */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.75rem' }}>
                {/* Professional Details */}
                {(selectedApplication.candidateInfo.currentCompany || selectedApplication.candidateInfo.currentTitle) && (
                  <div>
                    <label style={{ fontWeight: '600', color: '#6B7280', display: 'block', marginBottom: '0.625rem', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Current Role
                    </label>
                    <div style={{ color: '#111827', fontSize: '0.9375rem', fontWeight: '500' }}>
                      {[selectedApplication.candidateInfo.currentTitle, selectedApplication.candidateInfo.currentCompany].filter(Boolean).join(' at ')}
                    </div>
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ fontWeight: '600', color: '#6B7280', display: 'block', marginBottom: '0.375rem', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Experience
                    </label>
                    <div style={{ color: '#111827', fontSize: '0.9375rem', fontWeight: '500' }}>
                      {selectedApplication.candidateInfo.experienceYears ?
                        `${selectedApplication.candidateInfo.experienceYears} years` :
                        (selectedApplication.candidateInfo.yearsOfExperience ?
                          `${selectedApplication.candidateInfo.yearsOfExperience} years` :
                          <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>--</span>)}
                    </div>
                  </div>
                  <div>
                    <label style={{ fontWeight: '600', color: '#6B7280', display: 'block', marginBottom: '0.375rem', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Notice Period
                    </label>
                    <div style={{ color: '#111827', fontSize: '0.9375rem', fontWeight: '500' }}>
                      {selectedApplication.candidateInfo.noticePeriod || <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>--</span>}
                    </div>
                  </div>
                  <div>
                    <label style={{ fontWeight: '600', color: '#6B7280', display: 'block', marginBottom: '0.375rem', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Current CTC
                    </label>
                    <div style={{ color: '#111827', fontSize: '0.9375rem', fontWeight: '500' }}>
                      {selectedApplication.candidateInfo.currentCTC || <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>--</span>}
                    </div>
                  </div>
                  <div>
                    <label style={{ fontWeight: '600', color: '#6B7280', display: 'block', marginBottom: '0.375rem', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Expected CTC
                    </label>
                    <div style={{ color: '#111827', fontSize: '0.9375rem', fontWeight: '500' }}>
                      {selectedApplication.candidateInfo.expectedCTC || <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>--</span>}
                    </div>
                  </div>
                </div>

                <div>
                  <label style={{ 
                    fontWeight: '600', 
                    color: '#6B7280', 
                    display: 'block', 
                    marginBottom: '0.625rem', 
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Applied Date
                  </label>
                  <div style={{ 
                    color: '#111827', 
                    fontSize: '0.9375rem',
                    fontWeight: '500'
                  }}>
                    {selectedApplication.createdAt ?
                      new Date(selectedApplication.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      }) :
                      <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>Unknown</span>}
                  </div>
                </div>

                {/* Source & Profile Links */}
                {(selectedApplication.source || selectedApplication.candidateInfo?.linkedinUrl || selectedApplication.candidateInfo?.githubUrl || selectedApplication.candidateInfo?.portfolioUrl) && (
                  <div>
                    <label style={{ 
                      fontWeight: '600', 
                      color: '#6B7280', 
                      display: 'block', 
                      marginBottom: '0.625rem', 
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Source & Profiles
                    </label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {selectedApplication.source && selectedApplication.source !== 'direct' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ fontSize: '0.8125rem', color: '#6B7280' }}>Source:</span>
                          <span className={`source-badge source-${selectedApplication.source}`}>
                            {selectedApplication.source === 'linkedin' ? 'LinkedIn' :
                             selectedApplication.source === 'naukri' ? 'Naukri' :
                             selectedApplication.source === 'indeed' ? 'Indeed' :
                             selectedApplication.source === 'referral' ? 'Referral' :
                             selectedApplication.source === 'campus' ? 'Campus' :
                             selectedApplication.source === 'manual' ? 'Direct Sourced' :
                             selectedApplication.source.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                          </span>
                        </div>
                      )}
                      {selectedApplication.candidateInfo?.linkedinUrl && (
                        <a href={selectedApplication.candidateInfo.linkedinUrl} target="_blank" rel="noopener noreferrer"
                          style={{ fontSize: '0.8125rem', color: '#0A66C2', textDecoration: 'none', fontWeight: '500' }}>
                          LinkedIn Profile →
                        </a>
                      )}
                      {selectedApplication.candidateInfo?.githubUrl && (
                        <a href={selectedApplication.candidateInfo.githubUrl} target="_blank" rel="noopener noreferrer"
                          style={{ fontSize: '0.8125rem', color: '#24292F', textDecoration: 'none', fontWeight: '500' }}>
                          GitHub Profile →
                        </a>
                      )}
                      {selectedApplication.candidateInfo?.portfolioUrl && (
                        <a href={selectedApplication.candidateInfo.portfolioUrl} target="_blank" rel="noopener noreferrer"
                          style={{ fontSize: '0.8125rem', color: '#4F46E5', textDecoration: 'none', fontWeight: '500' }}>
                          Portfolio →
                        </a>
                      )}
                      {selectedApplication.referredBy && (
                        <div style={{ fontSize: '0.8125rem', color: '#374151' }}>
                          Referred by: <strong>{selectedApplication.referredBy}</strong>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Cross-role application visibility */}
                {Array.isArray(selectedApplication.candidateRoleHistory) && selectedApplication.candidateRoleHistory.length > 0 && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{
                      fontWeight: '600',
                      color: '#6B7280',
                      display: 'block',
                      marginBottom: '0.625rem',
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Applications Across Roles
                    </label>
                    <div style={{
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      background: '#fff'
                    }}>
                      {selectedApplication.candidateRoleHistory.map((historyItem) => (
                        <div
                          key={historyItem.applicationId}
                          style={{
                            padding: '0.75rem 1rem',
                            borderBottom: '1px solid #F3F4F6',
                            background: historyItem.isCurrentApplication ? '#EFF6FF' : 'white',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: '1rem'
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: '600', color: '#111827', fontSize: '0.875rem' }}>
                              {historyItem.jobTitle}
                              {historyItem.isCurrentApplication && (
                                <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#1D4ED8' }}>(Current)</span>
                              )}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>
                              Applied {historyItem.appliedAt ? new Date(historyItem.appliedAt).toLocaleDateString() : 'N/A'}
                              {historyItem.department ? ` • ${historyItem.department}` : ''}
                            </div>
                          </div>
                          <div style={{
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            color: '#374151',
                            textTransform: 'capitalize'
                          }}>
                            {(historyItem.applicationStatus || 'unknown').replace(/_/g, ' ')}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Assessment Progress Section */}
                {selectedApplication.assessmentProgress && (
                  <div>
                    <label style={{ 
                      fontWeight: '600', 
                      color: '#6B7280', 
                      display: 'block', 
                      marginBottom: '0.625rem', 
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Assessment Labs Progress
                    </label>
                    <div style={{
                      background: '#F9FAFB',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      padding: '1rem',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.75rem'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ 
                          color: '#111827', 
                          fontSize: '0.9375rem',
                          fontWeight: '600'
                        }}>
                          Labs Completed
                        </span>
                        <span style={{ 
                          color: '#3B82F6', 
                          fontSize: '1.125rem',
                          fontWeight: '700'
                        }}>
                          {selectedApplication.assessmentProgress.completedLabs} / {selectedApplication.assessmentProgress.totalLabs}
                        </span>
                      </div>
                      <div style={{
                        background: '#E5E7EB',
                        borderRadius: '4px',
                        height: '8px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          background: selectedApplication.assessmentProgress.progressPercentage === 100 ? '#10B981' : '#3B82F6',
                          height: '100%',
                          width: `${selectedApplication.assessmentProgress.progressPercentage}%`,
                          transition: 'width 0.3s ease',
                          borderRadius: '4px'
                        }}></div>
                      </div>
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        fontSize: '0.8125rem',
                        color: '#6B7280'
                      }}>
                        <span>Progress: {selectedApplication.assessmentProgress.progressPercentage}%</span>
                        <span style={{
                          padding: '0.25rem 0.625rem',
                          borderRadius: '12px',
                          background: selectedApplication.assessmentProgress.status === 'completed' ? '#D1FAE5' : '#DBEAFE',
                          color: selectedApplication.assessmentProgress.status === 'completed' ? '#065F46' : '#1E40AF',
                          fontWeight: '600',
                          fontSize: '0.75rem'
                        }}>
                          {selectedApplication.assessmentProgress.status === 'completed' ? 'Completed' : selectedApplication.assessmentProgress.status === 'in_progress' ? 'In Progress' : 'Pending'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {selectedApplication.assessmentScore && (
                  <div>
                    <label style={{ 
                      fontWeight: '600', 
                      color: '#6B7280', 
                      display: 'block', 
                      marginBottom: '0.625rem', 
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Assessment Score
                    </label>
                    <div style={{ 
                      fontSize: '1.125rem',
                      fontWeight: '700',
                      color: selectedApplication.assessmentScore >= 70 ? '#10B981' : selectedApplication.assessmentScore >= 50 ? '#F59E0B' : '#EF4444'
                    }}>
                      {selectedApplication.assessmentScore}%
                    </div>
                  </div>
                )}
              </div>
            </div>

                {/* Interview Details Section */}
                {interviewDetails && (
                  <div style={{
                    background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)',
                    border: '1px solid #3B82F6',
                    borderRadius: '8px',
                    padding: '1.5rem',
                    marginBottom: '1.5rem'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      marginBottom: '1.25rem',
                      gap: '0.75rem'
                    }}>
                      <div style={{
                        width: '8px',
                        height: '8px',
                        background: '#10B981',
                        borderRadius: '50%'
                      }}></div>
                      <label style={{ 
                        fontWeight: '700', 
                        color: '#1E40AF', 
                        fontSize: '0.9375rem',
                        letterSpacing: '0.025em',
                        textTransform: 'uppercase'
                      }}>
                        Interview Scheduled
                      </label>
                    </div>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: '1fr 1fr',
                      gap: '1.5rem'
                    }}>
                      <div>
                        <strong style={{ 
                          color: '#6B7280', 
                          fontSize: '0.75rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          display: 'block',
                          marginBottom: '0.5rem'
                        }}>Date & Time</strong>
                        <div style={{ color: '#111827', fontSize: '0.9375rem', fontWeight: '500' }}>
                          {new Date(interviewDetails.scheduledDate).toLocaleString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      </div>
                      <div>
                        <strong style={{ 
                          color: '#6B7280', 
                          fontSize: '0.75rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          display: 'block',
                          marginBottom: '0.5rem'
                        }}>Format</strong>
                        <div style={{ color: '#111827', fontSize: '0.9375rem', fontWeight: '500' }}>
                          {interviewDetails.type === 'video' ? 'Video Call' : 
                           interviewDetails.type === 'phone' ? 'Phone Call' : 'In-Person'}
                          {interviewDetails.meetingPlatform && ` (${interviewDetails.meetingPlatform})`}
                        </div>
                      </div>
                      {interviewDetails.interviewers && interviewDetails.interviewers.length > 0 && (
                        <div style={{ gridColumn: '1 / -1' }}>
                          <strong style={{ 
                            color: '#6B7280', 
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            display: 'block',
                            marginBottom: '0.5rem'
                          }}>Interviewer(s)</strong>
                          <div style={{ color: '#111827', fontSize: '0.9375rem', fontWeight: '500', marginBottom: '0.25rem' }}>
                            {interviewDetails.interviewers.map(i => i.name || i.email).join(', ')}
                          </div>
                          <div style={{ color: '#6B7280', fontSize: '0.8125rem' }}>
                            {interviewDetails.interviewers.map(i => i.email).join(', ')}
                          </div>
                        </div>
                      )}
                      {interviewDetails.meetingLink && (
                        <div style={{ gridColumn: '1 / -1' }}>
                          <strong style={{ 
                            color: '#6B7280', 
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            display: 'block',
                            marginBottom: '0.5rem'
                          }}>Meeting Link</strong>
                          <a 
                            href={interviewDetails.meetingLink} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ 
                              color: '#2563EB', 
                              fontSize: '0.9375rem',
                              fontWeight: '500',
                              textDecoration: 'none',
                              borderBottom: '1px solid #2563EB'
                            }}
                          >
                            Join Meeting →
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
            </>
            )}

            {activeProfileTab === 'activityLogs' && (
              <div style={{ 
                background: '#F9FAFB',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                padding: '1.75rem'
              }}>
                <label style={{ 
                  fontWeight: '700', 
                  color: '#111827', 
                  display: 'block', 
                  marginBottom: '1.25rem', 
                  fontSize: '0.9375rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Activity Logs
                </label>
                {selectedApplication.timeline && selectedApplication.timeline.length > 0 ? (
                  <div style={{ 
                    maxHeight: '550px', 
                    overflowY: 'auto',
                    paddingRight: '0.5rem'
                  }}>
                    {selectedApplication.timeline
                      .slice()
                      .reverse()
                      .map((item, index) => (
                        <div
                          key={index}
                          style={{
                            padding: '1rem',
                            borderLeft: '3px solid #3B82F6',
                            marginBottom: '1rem',
                            background: 'white',
                            borderRadius: '6px',
                            border: '1px solid #E5E7EB'
                          }}
                        >
                          <div style={{ 
                            fontSize: '0.75rem', 
                            color: '#6B7280', 
                            marginBottom: '0.5rem',
                            fontWeight: '500',
                            letterSpacing: '0.025em'
                          }}>
                            {new Date(item.timestamp).toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                          <div style={{ 
                            fontSize: '0.875rem', 
                            fontWeight: '600',
                            color: '#111827',
                            marginBottom: '0.5rem',
                            textTransform: 'capitalize',
                            letterSpacing: '0.025em'
                          }}>
                            {item.action.replace(/_/g, ' ')}
                          </div>
                          {item.description && (
                            <div style={{ 
                              fontSize: '0.875rem', 
                              color: '#374151',
                              whiteSpace: 'pre-wrap',
                              lineHeight: '1.6'
                            }}>
                              {item.description}
                            </div>
                          )}
                          {item.performedBy && (
                            <div style={{ 
                              fontSize: '0.8125rem', 
                              color: '#9CA3AF',
                              marginTop: '0.5rem',
                              fontStyle: 'italic'
                            }}>
                              by {typeof item.performedBy === 'object' ? (item.performedBy.fullName || item.performedBy.email) : 'System'}
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                ) : (
                  <p style={{ 
                    fontSize: '0.875rem', 
                    color: '#9CA3AF', 
                    fontStyle: 'italic',
                    textAlign: 'center',
                    padding: '2rem 1rem',
                    background: '#F9FAFB',
                    borderRadius: '6px'
                  }}>
                    No activity recorded yet
                  </p>
                )}
              </div>
            )}

            {activeProfileTab === 'evaluation' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {/* Rating Manager */}
                <div style={{ 
                  background: '#F9FAFB',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  padding: '1.75rem'
                }}>
                  <label style={{ 
                    fontWeight: '700', 
                    color: '#111827', 
                    display: 'block', 
                    marginBottom: '1.25rem', 
                    fontSize: '0.9375rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Rating & Evaluation
                  </label>
                  <RatingManager
                    ratings={selectedApplication.ratings || {}}
                    onRatingChange={handleRatingUpdate}
                  />
                </div>

                {/* Tags Manager */}
                <div style={{ 
                  background: '#F9FAFB',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  padding: '1.75rem'
                }}>
                  <label style={{ 
                    fontWeight: '700', 
                    color: '#111827', 
                    display: 'block', 
                    marginBottom: '1.25rem', 
                    fontSize: '0.9375rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Tags & Labels
                  </label>
                  <TagsManager
                    tags={selectedApplication.tags || []}
                    onTagsChange={handleTagsUpdate}
                  />
                </div>

                {/* Notes & Comments Section */}
                <div style={{ 
                  background: '#F9FAFB',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  padding: '1.75rem'
                }}>
                  <label style={{ 
                    fontWeight: '700', 
                    color: '#111827', 
                    display: 'block', 
                    marginBottom: '1.25rem', 
                    fontSize: '0.9375rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Notes & Internal Comments
                  </label>
                  <NotesSection
                    recruiterNotes={selectedApplication.recruiterNotes || []}
                    internalComments={selectedApplication.internalComments || []}
                    onAddNote={handleAddNote}
                    onAddComment={handleAddComment}
                  />
                </div>

                {/* Role Fit & Reassignment */}
                <div style={{
                  background: '#F9FAFB',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  padding: '1.75rem'
                }}>
                  <label style={{
                    fontWeight: '700',
                    color: '#111827',
                    display: 'block',
                    marginBottom: '1rem',
                    fontSize: '0.9375rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Role Fit Decision
                  </label>

                  <div style={{ marginBottom: '0.75rem', fontSize: '0.8125rem', color: '#4B5563' }}>
                    Mark candidate unfit for this role and either keep for future matching roles or move to another open role.
                  </div>

                  <textarea
                    value={roleFitNotes}
                    onChange={(e) => setRoleFitNotes(e.target.value)}
                    placeholder="Add notes for this decision (optional)"
                    style={{
                      width: '100%',
                      minHeight: '80px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '6px',
                      padding: '0.625rem',
                      fontSize: '0.875rem',
                      resize: 'vertical',
                      marginBottom: '0.75rem'
                    }}
                  />

                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                    <select
                      value={moveTargetJobId}
                      onChange={(e) => setMoveTargetJobId(e.target.value)}
                      style={{
                        minWidth: '320px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '6px',
                        padding: '0.5rem 0.625rem',
                        fontSize: '0.875rem',
                        background: 'white'
                      }}
                    >
                      <option value="">{loadingMoveJobs ? 'Loading roles...' : 'Select target role to move candidate'}</option>
                      {availableMoveJobs.map(jobOption => (
                        <option key={jobOption._id} value={jobOption._id}>
                          {jobOption.title} {jobOption.department ? `(${jobOption.department})` : ''}
                        </option>
                      ))}
                    </select>

                    <button
                      onClick={() => handleRoleFitDecision('keep')}
                      disabled={processingRoleFit}
                      style={{
                        border: '1px solid #D1D5DB',
                        background: 'white',
                        color: '#374151',
                        borderRadius: '6px',
                        padding: '0.5rem 0.875rem',
                        fontSize: '0.8125rem',
                        fontWeight: '600',
                        cursor: processingRoleFit ? 'not-allowed' : 'pointer',
                        opacity: processingRoleFit ? 0.6 : 1
                      }}
                    >
                      Mark Unfit & Keep
                    </button>

                    <button
                      onClick={() => handleRoleFitDecision('move')}
                      disabled={processingRoleFit || !moveTargetJobId}
                      style={{
                        border: 'none',
                        background: '#2563EB',
                        color: 'white',
                        borderRadius: '6px',
                        padding: '0.5rem 0.875rem',
                        fontSize: '0.8125rem',
                        fontWeight: '600',
                        cursor: (processingRoleFit || !moveTargetJobId) ? 'not-allowed' : 'pointer',
                        opacity: (processingRoleFit || !moveTargetJobId) ? 0.6 : 1
                      }}
                    >
                      Mark Unfit & Move
                    </button>
                  </div>

                  {selectedApplication.roleFitDecision?.verdict === 'unfit' && (
                    <div style={{ fontSize: '0.8125rem', color: '#6B7280' }}>
                      Last decision: {selectedApplication.roleFitDecision.action?.replace(/_/g, ' ') || 'unfit'} on{' '}
                      {selectedApplication.roleFitDecision.decidedAt
                        ? new Date(selectedApplication.roleFitDecision.decidedAt).toLocaleString()
                        : 'N/A'}
                    </div>
                  )}
                </div>

                {/* Needs Manager Review Flag */}
                <div style={{ 
                  background: '#F9FAFB',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  padding: '1.75rem'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label style={{ 
                      fontWeight: '700', 
                      color: '#111827', 
                      fontSize: '0.9375rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Manager Review
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '0.75rem' }}>
                      <input
                        type="checkbox"
                        checked={selectedApplication.needsManagerReview || false}
                        onChange={async (e) => {
                          // Preserve scroll position in drawer
                          const currentScroll = drawerContentRef.current ? drawerContentRef.current.scrollTop : 0;
                          scrollPositionRef.current = currentScroll;
                          
                          if (drawerContentRef.current) {
                            drawerContentRef.current.setAttribute('data-saved-scroll', currentScroll.toString());
                          }

                          try {
                            const response = await jobsAPI.toggleManagerReview(jobId, selectedApplication._id, e.target.checked);
                            toastr.success(e.target.checked ? 'Flagged for manager review' : 'Removed from manager review');
                            
                            // Update selectedApplication in place to avoid drawer re-render
                            if (response.data && response.data.application) {
                              setSelectedApplication(prev => ({
                                ...prev,
                                needsManagerReview: response.data.application.needsManagerReview,
                                reviewedBy: response.data.application.reviewedBy,
                                reviewedAt: response.data.application.reviewedAt
                              }));
                              
                              // Update applications list in place
                              setApplications(prev => prev.map(app => 
                                app._id === selectedApplication._id 
                                  ? { ...app, needsManagerReview: response.data.application.needsManagerReview }
                                  : app
                              ));
                            }
                            
                            // Aggressively restore scroll
                            const restoreScroll = () => {
                              if (drawerContentRef.current && currentScroll > 0) {
                                drawerContentRef.current.scrollTop = currentScroll;
                                drawerContentRef.current.setAttribute('data-saved-scroll', currentScroll.toString());
                              }
                            };
                            restoreScroll();
                            requestAnimationFrame(restoreScroll);
                            setTimeout(restoreScroll, 0);
                            setTimeout(restoreScroll, 50);
                            setTimeout(restoreScroll, 100);
                            
                            // Immediately restore scroll after state update
                            requestAnimationFrame(() => {
                              if (drawerContentRef.current && currentScroll > 0) {
                                drawerContentRef.current.scrollTop = currentScroll;
                                setTimeout(() => {
                                  if (drawerContentRef.current) {
                                    drawerContentRef.current.scrollTop = currentScroll;
                                  }
                                }, 50);
                              }
                            });
                          } catch (error) {
                            console.error('Failed to update manager review flag:', error);
                            toastr.error('Failed to update manager review flag');
                          }
                        }}
                        style={{ marginRight: '0.5rem', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '0.875rem', color: '#374151', fontWeight: '500' }}>
                        Needs Manager Review
                      </span>
                    </label>
                  </div>
                  {selectedApplication.needsManagerReview && selectedApplication.reviewedBy && (
                    <div style={{ 
                      fontSize: '0.8125rem', 
                      color: '#6B7280', 
                      marginTop: '0.75rem',
                      padding: '0.5rem',
                      background: '#F3F4F6',
                      borderRadius: '4px'
                    }}>
                      Flagged by {typeof selectedApplication.reviewedBy === 'object' ? (selectedApplication.reviewedBy.fullName || selectedApplication.reviewedBy.email) : 'Unknown'} on {selectedApplication.reviewedAt ? new Date(selectedApplication.reviewedAt).toLocaleDateString() : ''}
                    </div>
                  )}
                </div>

              </div>
            )}
          </Drawer>
      )}

      {/* Resume Preview Modal */}
      {resumePreview.visible && (
        <div
          onClick={closeResumePreview}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.6)', zIndex: 10000,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white', borderRadius: '12px',
              width: '90vw', maxWidth: '900px', height: '85vh',
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
            }}
          >
            {/* Header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '1rem 1.5rem', borderBottom: '1px solid #E5E7EB'
            }}>
              <span style={{ fontWeight: 600, fontSize: '0.95rem', color: '#111827' }}>
                {resumePreview.filename}
              </span>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                {resumePreview.url && (
                  <a
                    href={resumePreview.url}
                    download={resumePreview.filename}
                    style={{
                      background: '#3B82F6', color: 'white', border: 'none',
                      padding: '0.4rem 1rem', borderRadius: '6px', cursor: 'pointer',
                      fontSize: '0.8rem', fontWeight: 500, textDecoration: 'none'
                    }}
                  >
                    Download
                  </a>
                )}
                <button
                  onClick={closeResumePreview}
                  style={{
                    background: '#F3F4F6', color: '#374151', border: 'none',
                    width: '32px', height: '32px', borderRadius: '6px', cursor: 'pointer',
                    fontSize: '1.1rem', display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}
                >
                  x
                </button>
              </div>
            </div>
            {/* Body */}
            <div style={{ flex: 1, overflow: 'hidden' }}>
              {resumePreview.loading ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#6B7280' }}>
                  Loading resume...
                </div>
              ) : resumePreview.url ? (
                resumePreview.filename.toLowerCase().endsWith('.pdf') ? (
                  <iframe
                    src={resumePreview.url}
                    title="Resume Preview"
                    style={{ width: '100%', height: '100%', border: 'none' }}
                  />
                ) : (resumePreview.filename.toLowerCase().endsWith('.doc') || resumePreview.filename.toLowerCase().endsWith('.docx')) ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '1rem', color: '#6B7280' }}>
                    <div style={{ fontSize: '3rem' }}>doc</div>
                    <p style={{ fontSize: '0.95rem' }}>Word documents cannot be previewed in-browser.</p>
                    <a
                      href={resumePreview.url}
                      download={resumePreview.filename}
                      style={{
                        background: '#3B82F6', color: 'white',
                        padding: '0.625rem 1.5rem', borderRadius: '6px',
                        textDecoration: 'none', fontWeight: 500, fontSize: '0.875rem'
                      }}
                    >
                      Download to View
                    </a>
                  </div>
                ) : (
                  <iframe
                    src={resumePreview.url}
                    title="Resume Preview"
                    style={{ width: '100%', height: '100%', border: 'none' }}
                  />
                )
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Resume status bar — shown in InterviewScheduler modal
 */
const ResumeStatusBar = ({ application, jobId }) => {
  const [uploading, setUploading] = useState(false);
  const [resumeState, setResumeState] = useState(application?.resume || null);
  const fileRef = useRef(null);

  const hasResume = !!(resumeState && (resumeState.gcsPath || resumeState.fileUrl));

  const handlePreview = async () => {
    try {
      const res = await jobsAPI.downloadResume(jobId, application._id);
      if (res.data?.downloadUrl) {
        window.open(res.data.downloadUrl, '_blank');
      } else {
        // Blob fallback
        const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/pdf' });
        window.open(URL.createObjectURL(blob), '_blank');
      }
    } catch {
      toastr.error('Failed to open resume');
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('resume', file);
      formData.append('applicationId', application._id);
      formData.append('jobId', jobId);
      const res = await jobsAPI.uploadCandidateResume(formData);
      const newResume = {
        filename: res.data?.filename || file.name,
        fileUrl: res.data?.fileUrl,
        gcsPath: res.data?.gcsPath,
        uploadedAt: new Date()
      };
      setResumeState(newResume);
      toastr.success('Resume uploaded');
    } catch {
      toastr.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const bg = hasResume ? '#ECFDF5' : '#FFFBEB';
  const border = hasResume ? '#10B981' : '#F59E0B';
  const color = hasResume ? '#065F46' : '#92400E';
  const btnStyle = { background: 'none', border: 'none', color: '#3B82F6', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 500, padding: '2px 6px', borderRadius: '4px' };

  return (
    <div style={{ marginBottom: '1rem', padding: '0.65rem 0.85rem', borderRadius: '6px', background: bg, border: `1px solid ${border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
        <span style={{ fontSize: '0.82rem', fontWeight: 500, color, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {hasResume ? (resumeState.filename || 'Resume attached') : 'No resume attached'}
        </span>
      </div>
      <div style={{ display: 'flex', gap: '0.25rem', flexShrink: 0 }}>
        {hasResume ? (
          <button type="button" onClick={handlePreview} style={btnStyle}>View</button>
        ) : (
          <>
            <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" hidden onChange={handleUpload} />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} style={{ ...btnStyle, color: '#92400E', fontWeight: 600 }}>
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

/**
 * InterviewScheduler Modal Component
 */
const InterviewScheduler = ({ application, jobId, onClose, onScheduled, existingInterview: propExistingInterview }) => {
  const { user } = useAuth(); // Get current user to prevent self-selection
  const [interviewDate, setInterviewDate] = useState('');
  const [interviewTime, setInterviewTime] = useState('');
  const [interviewType, setInterviewType] = useState('video');
  const [meetingLink, setMeetingLink] = useState('');
  const [meetingPlatform, setMeetingPlatform] = useState('teams'); // Default to Microsoft Teams
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedInterviewers, setSelectedInterviewers] = useState([]); // NEW: Selected interviewers array
  const [interviewerQuery, setInterviewerQuery] = useState(''); // NEW: Autocomplete search query
  const [interviewerSuggestions, setInterviewerSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false); // NEW: Show/hide dropdown
  const [searchingInterviewers, setSearchingInterviewers] = useState(false); // NEW: Loading state
  const [frequentInterviewers, setFrequentInterviewers] = useState([]); // NEW: Frequently used interviewers
  const [loadingFrequent, setLoadingFrequent] = useState(true); // NEW: Loading frequent interviewers
  const [submitting, setSubmitting] = useState(false);
  const [autoGenerateMeeting, setAutoGenerateMeeting] = useState(true); // Auto-generate Teams meeting by default
  const [existingInterview, setExistingInterview] = useState(propExistingInterview || null); // UX-38: Existing interview data
  const [showExistingInterviewDrawer, setShowExistingInterviewDrawer] = useState(false); // UX-38: Show existing interview drawer
  const [pendingInterviewData, setPendingInterviewData] = useState(null); // UX-38: Store the new interview data
  
  // Get process status for display
  const processStatus = application ? getProcessStatus(application) : null;

  // UX-38: Load existing interview data if rescheduling
  useEffect(() => {
    if (existingInterview) {
      const interviewDate = new Date(existingInterview.scheduledDate);
      setInterviewDate(interviewDate.toISOString().split('T')[0]);
      setInterviewTime(interviewDate.toTimeString().slice(0, 5));
      setInterviewType(existingInterview.type || 'video');
      setMeetingLink(existingInterview.meetingLink || '');
      setLocation(existingInterview.location || '');
      setNotes(existingInterview.notes || '');
      
      // Load existing interviewers
      if (existingInterview.interviewers && existingInterview.interviewers.length > 0) {
        const interviewers = existingInterview.interviewers.map(i => ({
          id: i.userId || i.email,
          name: i.name || i.interviewerName,
          email: i.email || i.interviewerEmail,
          jobTitle: i.jobTitle,
          department: i.department
        }));
        setSelectedInterviewers(interviewers);
      }
    }
  }, [existingInterview]);

  // Load frequently used interviewers on mount
  useEffect(() => {
    const loadFrequentInterviewers = async () => {
      try {
        const response = await jobsAPI.getFrequentInterviewers(5);
        // BL-28: Filter out current recruiter from frequent interviewers
        const recruiterEmail = user?.email?.toLowerCase();
        const filtered = (response.data.interviewers || []).filter(
          i => i.interviewerEmail?.toLowerCase() !== recruiterEmail
        );
        setFrequentInterviewers(filtered);
      } catch (err) {
        console.error('[InterviewScheduler] Failed to load frequent interviewers:', err);
      } finally {
        setLoadingFrequent(false);
      }
    };
    loadFrequentInterviewers();
  }, [user]);

  // Debounced interviewer search
  useEffect(() => {
    if (interviewerQuery.trim().length < 2) {
      setInterviewerSuggestions([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setSearchingInterviewers(true);
        const response = await jobsAPI.searchAzureADUsers(interviewerQuery);
        // BL-28: Filter out current recruiter from search results
        const recruiterEmail = user?.email?.toLowerCase();
        const filteredUsers = (response.data.users || []).filter(
          u => u.email?.toLowerCase() !== recruiterEmail
        );
        setInterviewerSuggestions(filteredUsers);
        setShowSuggestions(true);
      } catch (err) {
        console.error('[InterviewScheduler] Failed to search users:', err);
        setInterviewerSuggestions([]);
      } finally {
        setSearchingInterviewers(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [interviewerQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSchedule = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    // Extract emails from selected interviewers (define outside try for catch block)
    const interviewers = selectedInterviewers.map(interviewer => interviewer.email);

    try {
      // Combine date and time
      const scheduledDate = new Date(`${interviewDate}T${interviewTime}`);

      if (interviewers.length === 0) {
        toastr.error('Please select at least one person to attend the interview');
        setSubmitting(false);
        return;
      }

      // BL-28: Frontend validation - prevent recruiter from selecting themselves
      const recruiterEmail = user?.email?.toLowerCase();
      const selfSelected = interviewers.some(email => email.toLowerCase() === recruiterEmail);
      if (selfSelected) {
        toastr.error('You cannot schedule yourself as an interviewer. Please select a different person.');
        setSubmitting(false);
        return;
      }

      // UX-38: If rescheduling, use reschedule API; otherwise schedule new
      if (existingInterview) {
        // Reschedule existing interview with updated details
        await jobsAPI.rescheduleInterview(existingInterview._id || existingInterview.id, {
          newDate: scheduledDate.toISOString(),
          newDuration: 60,
          interviewerEmails: interviewers,
          type: interviewType,
          meetingLink: interviewType === 'video' && !autoGenerateMeeting ? meetingLink : null,
          location: interviewType === 'in-person' ? location : null,
          notes: notes,
          reason: `Interview rescheduled and updated by recruiter`
        });
        
        toastr.success('Interview rescheduled successfully!');
        onScheduled();
      } else {
        // Store pending interview data for potential reschedule
        const interviewData = {
          scheduledDate: scheduledDate.toISOString(),
          type: interviewType,
          meetingLink: interviewType === 'video' && !autoGenerateMeeting ? meetingLink : null,
          meetingPlatform: interviewType === 'video' ? meetingPlatform : null,
          autoGenerateMeeting: interviewType === 'video' && meetingPlatform === 'teams' && autoGenerateMeeting,
          location: interviewType === 'in-person' ? location : null,
          notes,
          duration: 60,
          interviewerEmails: interviewers // Send interviewer emails to backend
        };

        await jobsAPI.scheduleInterview(jobId, application._id, interviewData);

        toastr.success(autoGenerateMeeting && meetingPlatform === 'teams' ? 
          'Interview scheduled! Teams meeting created automatically.' : 
          'Interview scheduled successfully!');
        onScheduled();
      }
    } catch (err) {
      console.error('Failed to schedule interview:', err);
      
      // UX-38: Handle duplicate interview - show drawer instead of error
      if (err.response?.status === 409 && err.response?.data?.code === 'DUPLICATE_INTERVIEW') {
        setExistingInterview(err.response.data.existingInterview);
        setPendingInterviewData({
          scheduledDate: new Date(`${interviewDate}T${interviewTime}`).toISOString(),
          type: interviewType,
          meetingLink: interviewType === 'video' && !autoGenerateMeeting ? meetingLink : null,
          meetingPlatform: interviewType === 'video' ? meetingPlatform : null,
          autoGenerateMeeting: interviewType === 'video' && meetingPlatform === 'teams' && autoGenerateMeeting,
          location: interviewType === 'in-person' ? location : null,
          notes,
          duration: 60,
          interviewerEmails: interviewers
        });
        setShowExistingInterviewDrawer(true);
        setSubmitting(false);
        return;
      }
      
      toastr.error(err.response?.data?.error || 'Failed to schedule interview');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      isOpen={true}
      onClose={onClose}
      title="Schedule Interview"
      width="md"
      footer={
        <>
          <button
            type="button"
            className="drawer-btn drawer-btn-secondary"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="drawer-btn drawer-btn-primary"
            onClick={handleSchedule}
            disabled={submitting}
          >
            {submitting ? 'Scheduling...' : 'Schedule Interview'}
          </button>
        </>
      }
    >
      {/* Process Status Indicator */}
      {processStatus && (
        <div style={{
          marginBottom: '1rem',
          padding: '0.75rem',
          borderRadius: '6px',
          background: processStatus.status === 'ready' ? '#D1FAE5' : 
                      processStatus.status === 'pending' ? '#FEF3C7' : '#FEE2E2',
          border: `1px solid ${processStatus.status === 'ready' ? '#10B981' : 
                                    processStatus.status === 'pending' ? '#F59E0B' : '#EF4444'}`
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: '0.875rem',
            fontWeight: '500',
            color: processStatus.status === 'ready' ? '#065F46' : 
                   processStatus.status === 'pending' ? '#92400E' : '#991B1B'
          }}>
            <ProcessStatus application={application} compact={true} />
            {processStatus.status === 'pending' && (
              <span>• {processStatus.message}</span>
            )}
          </div>
        </div>
      )}
      
      <div className="modal-subtitle" style={{ marginBottom: '1rem', color: '#6B7280' }}>
        {application.candidateInfo.firstName} {application.candidateInfo.lastName}
      </div>

      {/* Resume status */}
      <ResumeStatusBar application={application} jobId={jobId} />

      <form onSubmit={handleSchedule}>
          <div className="form-group">
            <label>Interview Date *</label>
            <input
              type="date"
              value={interviewDate}
              onChange={(e) => setInterviewDate(e.target.value)}
              required
              min={new Date().toISOString().split('T')[0]}
            />
          </div>

          <div className="form-group">
            <label>Interview Time *</label>
            <input
              type="time"
              value={interviewTime}
              onChange={(e) => setInterviewTime(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label>Interviewer(s) *</label>
            
            {/* FIX UX-35: Loading State for Frequent Interviewers */}
            {loadingFrequent && (
              <div style={{ 
                marginBottom: '0.75rem', 
                padding: '0.75rem', 
                background: '#F9FAFB', 
                border: '1px solid #E5E7EB', 
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid #E5E7EB',
                  borderTopColor: '#4F46E5',
                  borderRadius: '50%'
                }}></div>
                <span style={{ color: '#6B7280', fontSize: '0.875rem' }}>
                  Loading frequently used interviewers...
                </span>
              </div>
            )}
            
            
            {/* Selected Interviewers (Chips) */}
            {selectedInterviewers.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
                {selectedInterviewers.map((interviewer, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      background: '#EFF6FF',
                      border: '1px solid #3B82F6',
                      borderRadius: '20px',
                      padding: '0.375rem 0.75rem',
                      fontSize: '0.875rem',
                      color: '#1E40AF',
                      gap: '0.5rem'
                    }}
                  >
                    <span><strong>{interviewer.name}</strong></span>
                    <span style={{ color: '#6B7280', fontSize: '0.8rem' }}>({interviewer.email})</span>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedInterviewers(selectedInterviewers.filter((_, i) => i !== index));
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#EF4444',
                        cursor: 'pointer',
                        padding: '0',
                        fontSize: '1.1rem',
                        lineHeight: '1'
                      }}
                      title="Remove"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Autocomplete Input */}
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                value={interviewerQuery}
                onChange={(e) => setInterviewerQuery(e.target.value)}
                onFocus={() => {
                  // Show dropdown when focused if we have frequent interviewers or suggestions
                  if (frequentInterviewers.length > 0 || interviewerSuggestions.length > 0) {
                    setShowSuggestions(true);
                  }
                }}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)} // Delay to allow click
                placeholder="Type name or email to search..."
                style={{
                  width: '100%',
                  padding: '0.625rem',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '0.95rem'
                }}
              />
              {searchingInterviewers && (
                <div style={{
                  position: 'absolute',
                  right: '10px',
                  top: '0',
                  bottom: '0',
                  display: 'flex',
                  alignItems: 'center',
                  color: '#6B7280',
                  fontSize: '0.875rem'
                }}>
                  Searching...
                </div>
              )}

              {/* Dropdown - Shows frequent interviewers when no search query, search results when available */}
              {showSuggestions && (
                (interviewerQuery.trim() === '' && !loadingFrequent && frequentInterviewers.length > 0) ||
                (interviewerQuery.trim() !== '' && interviewerSuggestions.length > 0)
              ) && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: '0',
                  right: '0',
                  background: 'white',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  marginTop: '4px',
                  maxHeight: '250px',
                  overflowY: 'auto',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  zIndex: 1000
                }}>
                  {/* Show frequent interviewers when no search query */}
                  {interviewerQuery.trim() === '' && !loadingFrequent && frequentInterviewers.length > 0 && (
                    <>
                      <div style={{
                        padding: '0.5rem 1rem',
                        background: '#F0F9FF',
                        borderBottom: '1px solid #BFDBFE',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        color: '#1E40AF',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Frequently Used Interviewers
                      </div>
                      {frequentInterviewers.map((interviewer, index) => {
                        const alreadySelected = selectedInterviewers.some(i => i.email === interviewer.interviewerEmail);
                        return (
                          <div
                            key={`frequent-${index}`}
                            onClick={() => {
                              if (!alreadySelected) {
                                setSelectedInterviewers([...selectedInterviewers, {
                                  id: interviewer.interviewerEmail,
                                  name: interviewer.interviewerName,
                                  email: interviewer.interviewerEmail,
                                  jobTitle: interviewer.jobTitle,
                                  department: interviewer.department
                                }]);
                                setInterviewerQuery('');
                                setShowSuggestions(false);
                              }
                            }}
                            style={{
                              padding: '0.75rem 1rem',
                              cursor: alreadySelected ? 'not-allowed' : 'pointer',
                              borderBottom: index < frequentInterviewers.length - 1 ? '1px solid #F3F4F6' : (interviewerSuggestions.length > 0 ? '1px solid #E5E7EB' : 'none'),
                              background: alreadySelected ? '#F3F4F6' : 'white',
                              opacity: alreadySelected ? 0.6 : 1
                            }}
                            onMouseEnter={(e) => !alreadySelected && (e.target.style.background = '#F9FAFB')}
                            onMouseLeave={(e) => !alreadySelected && (e.target.style.background = 'white')}
                          >
                            <div style={{ fontWeight: '600', color: '#111827', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              {interviewer.interviewerName}
                              {alreadySelected && <span style={{ 
                                background: '#D1FAE5',
                                color: '#065F46',
                                fontSize: '0.6875rem',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontWeight: '600',
                                textTransform: 'uppercase',
                                letterSpacing: '0.3px'
                              }}>Selected</span>}
                              {interviewer.timesSelectedByYou > 0 && (
                                <span style={{ 
                                  fontSize: '0.7rem', 
                                  background: '#3B82F6', 
                                  color: 'white', 
                                  padding: '1px 5px', 
                                  borderRadius: '10px',
                                  marginLeft: 'auto'
                                }}>
                                  {interviewer.timesSelectedByYou}
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize: '0.8rem', color: '#6B7280', marginTop: '0.1rem' }}>
                              {interviewer.interviewerEmail}
                            </div>
                            {(interviewer.jobTitle || interviewer.department) && (
                              <div style={{ fontSize: '0.75rem', color: '#9CA3AF', marginTop: '0.1rem' }}>
                                {interviewer.jobTitle || ''}
                                {interviewer.jobTitle && interviewer.department && ' • '}
                                {interviewer.department || ''}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </>
                  )}

                  {/* Show search results when available */}
                  {interviewerQuery.trim() !== '' && interviewerSuggestions.length > 0 && (
                    <>
                      {interviewerQuery.trim() !== '' && frequentInterviewers.length > 0 && (
                        <div style={{
                          padding: '0.5rem 1rem',
                          background: '#F9FAFB',
                          borderTop: '1px solid #E5E7EB',
                          borderBottom: '1px solid #E5E7EB',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          color: '#6B7280',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Search Results
                        </div>
                      )}
                      {interviewerSuggestions.map((user, index) => {
                    const alreadySelected = selectedInterviewers.some(i => i.email === user.email);
                    return (
                      <div
                        key={index}
                        onClick={() => {
                          if (!alreadySelected) {
                            setSelectedInterviewers([...selectedInterviewers, user]);
                            setInterviewerQuery('');
                            setInterviewerSuggestions([]);
                          }
                        }}
                        style={{
                          padding: '0.75rem 1rem',
                          cursor: alreadySelected ? 'not-allowed' : 'pointer',
                          borderBottom: index < interviewerSuggestions.length - 1 ? '1px solid #F3F4F6' : 'none',
                          background: alreadySelected ? '#F3F4F6' : 'white',
                          opacity: alreadySelected ? 0.6 : 1
                        }}
                        onMouseEnter={(e) => !alreadySelected && (e.target.style.background = '#F9FAFB')}
                        onMouseLeave={(e) => !alreadySelected && (e.target.style.background = 'white')}
                      >
                        <div style={{ fontWeight: '600', color: '#111827', fontSize: '0.9rem' }}>
                          {user.name} {alreadySelected && <span style={{ 
                            background: '#D1FAE5',
                            color: '#065F46',
                            fontSize: '0.6875rem',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            letterSpacing: '0.3px',
                            marginLeft: '0.5rem'
                          }}>Selected</span>}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#6B7280', marginTop: '0.1rem' }}>
                          {user.email}
                        </div>
                        {user.jobTitle && (
                          <div style={{ fontSize: '0.75rem', color: '#9CA3AF', marginTop: '0.1rem' }}>
                            {user.jobTitle}
                            {user.department && ` • ${user.department}`}
                          </div>
                        )}
                      </div>
                    );
                      })}
                    </>
                  )}
                </div>
              )}
            </div>

            <small style={{ display: 'block', marginTop: '0.5rem', color: '#6B7280', fontSize: '0.85rem' }}>
              {selectedInterviewers.length === 0 
                ? (frequentInterviewers.length > 0 
                    ? 'Click to see frequently used interviewers, or type to search' 
                    : 'Start typing to search for interviewers')
                : `${selectedInterviewers.length} interviewer(s) selected`}
            </small>
          </div>

          <div className="form-group">
            <label>Interview Type *</label>
            <select
              value={interviewType}
              onChange={(e) => setInterviewType(e.target.value)}
              required
            >
              <option value="video">Video Call</option>
              <option value="phone">Phone Call</option>
              <option value="in-person">In-Person</option>
            </select>
          </div>

          {interviewType === 'video' && (
            <>
              <div className="form-group">
                <label>Meeting Platform</label>
                <select
                  value={meetingPlatform}
                  onChange={(e) => {
                    setMeetingPlatform(e.target.value);
                    // Auto-enable meeting generation for Teams
                    if (e.target.value === 'teams') {
                      setAutoGenerateMeeting(true);
                    } else {
                      setAutoGenerateMeeting(false);
                    }
                  }}
                >
                  <option value="teams">Microsoft Teams</option>
                  <option value="google-meet">Google Meet</option>
                  <option value="zoom">Zoom</option>
                  <option value="other">Other</option>
                </select>
              </div>


              {(!autoGenerateMeeting || meetingPlatform !== 'teams') && (
                <div className="form-group">
                  <label>Meeting Link {meetingPlatform === 'teams' && !autoGenerateMeeting ? '' : '*'}</label>
                  <input
                    type="url"
                    value={meetingLink}
                    onChange={(e) => setMeetingLink(e.target.value)}
                    placeholder={
                      meetingPlatform === 'teams' ? 'https://teams.microsoft.com/...' :
                      meetingPlatform === 'zoom' ? 'https://zoom.us/j/...' :
                      meetingPlatform === 'google-meet' ? 'https://meet.google.com/...' :
                      'https://...'
                    }
                    required={!autoGenerateMeeting && meetingPlatform !== 'teams'}
                  />
                </div>
              )}
            </>
          )}

          {interviewType === 'in-person' && (
            <div className="form-group">
              <label>Location</label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Office address..."
                required
              />
            </div>
          )}

          <div className="form-group">
            <label>Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows="3"
              placeholder="Any additional notes for the candidate..."
            />
          </div>
        </form>

        {/* UX-38: Existing Interview Drawer - Shows when duplicate interview is detected */}
        {showExistingInterviewDrawer && existingInterview && (
          <ExistingInterviewHandler
            existingInterview={existingInterview}
            pendingInterviewData={pendingInterviewData}
            jobId={jobId}
            applicationId={application._id}
            onRescheduled={async () => {
              setShowExistingInterviewDrawer(false);
              setExistingInterview(null);
              setPendingInterviewData(null);
              // Refresh interview details after rescheduling
              if (application && application._id) {
                // Note: loadInterviewDetails is not accessible here, but onScheduled will handle it
              }
              onScheduled();
            }}
            onCancelled={() => {
              setShowExistingInterviewDrawer(false);
              setExistingInterview(null);
              setPendingInterviewData(null);
              // Retry scheduling after cancellation
              handleSchedule({ preventDefault: () => {} });
            }}
            onSkip={() => {
              setShowExistingInterviewDrawer(false);
              setExistingInterview(null);
              setPendingInterviewData(null);
              onClose();
            }}
          />
        )}
    </Drawer>
  );
};

/**
 * UX-38: Existing Interview Handler Drawer
 * Shows when user tries to schedule an interview but one already exists
 */
const ExistingInterviewHandler = ({ existingInterview, pendingInterviewData, jobId, applicationId, onRescheduled, onCancelled, onSkip }) => {
  const [action, setAction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');
  const [reason, setReason] = useState('');

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleReschedule = async () => {
    if (!newDate || !newTime) {
      toastr.error('Please select a new date and time');
      return;
    }

    try {
      setLoading(true);
      const scheduledDate = new Date(`${newDate}T${newTime}`);
      await jobsAPI.rescheduleInterview(existingInterview.id, {
        newDate: scheduledDate.toISOString(),
        reason: reason || 'Interview rescheduled by recruiter'
      });
      
      toastr.success('Interview rescheduled successfully');
      onRescheduled();
    } catch (err) {
      console.error('Failed to reschedule:', err);
      toastr.error(err.response?.data?.error || 'Failed to reschedule interview');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    try {
      setLoading(true);
      await jobsAPI.cancelInterview(existingInterview.id, reason || 'Cancelled to schedule new interview');
      toastr.success('Existing interview cancelled successfully');
      onCancelled();
    } catch (err) {
      console.error('Failed to cancel:', err);
      toastr.error(err.response?.data?.error || 'Failed to cancel interview');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      isOpen={true}
      onClose={() => !loading && onSkip()}
      title="Existing Interview Found"
      width="md"
      footer={
        <>
          <button
            type="button"
            className="drawer-btn drawer-btn-secondary"
            onClick={onSkip}
            disabled={loading}
          >
            {action ? 'Go Back' : 'Skip'}
          </button>
          {action === 'reschedule' && (
            <button
              type="button"
              className="drawer-btn drawer-btn-primary"
              onClick={handleReschedule}
              disabled={loading || !newDate || !newTime}
            >
              {loading ? 'Rescheduling...' : 'Reschedule Interview'}
            </button>
          )}
          {action === 'cancel' && (
            <button
              type="button"
              className="drawer-btn drawer-btn-danger"
              onClick={handleCancel}
              disabled={loading}
            >
              {loading ? 'Cancelling...' : 'Cancel Existing Interview'}
            </button>
          )}
        </>
      }
    >
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{
          background: '#FEF3C7',
          border: '1px solid #F59E0B',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '1.5rem'
        }}>
          <div style={{ fontWeight: '600', marginBottom: '0.5rem', color: '#92400E' }}>
            An interview is already scheduled
          </div>
          <div style={{ fontSize: '0.9rem', color: '#78350F' }}>
            You may have forgotten about this existing interview, or you may want to update it.
          </div>
        </div>

        <div style={{
          background: '#F9FAFB',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          padding: '1.5rem',
          marginBottom: '1.5rem'
        }}>
          <div style={{ fontWeight: '600', marginBottom: '1rem', fontSize: '1.1rem', color: '#111827' }}>
            Existing Interview Details
          </div>
          
          <div style={{ marginBottom: '0.75rem' }}>
            <strong style={{ color: '#374151' }}>Date & Time:</strong>
            <div style={{ color: '#6B7280', marginTop: '0.25rem' }}>
              {formatDateTime(existingInterview.scheduledDate)}
            </div>
          </div>

          {existingInterview.interviewers && existingInterview.interviewers.length > 0 && (
            <div style={{ marginBottom: '0.75rem' }}>
              <strong style={{ color: '#374151' }}>Interviewer(s):</strong>
              <div style={{ color: '#6B7280', marginTop: '0.25rem' }}>
                {existingInterview.interviewers.map((i, idx) => (
                  <span key={idx}>
                    {i.name || i.email}
                    {idx < existingInterview.interviewers.length - 1 && ', '}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {!action && (
          <div>
            <div style={{ fontWeight: '600', marginBottom: '1rem', color: '#111827' }}>
              What would you like to do?
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <button
                type="button"
                onClick={() => setAction('reschedule')}
                className="drawer-btn"
                style={{
                  padding: '1rem 1.25rem',
                  background: '#FFFFFF',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontSize: '0.9375rem',
                  color: '#374151',
                  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#2563EB';
                  e.target.style.background = '#EFF6FF';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#D1D5DB';
                  e.target.style.background = '#FFFFFF';
                }}
              >
                <div style={{ fontWeight: '600', marginBottom: '0.25rem', color: '#111827' }}>Update/Reschedule</div>
                <div style={{ fontSize: '0.875rem', color: '#6B7280' }}>
                  Change the date, time, or other details of the existing interview
                </div>
              </button>

              <button
                type="button"
                onClick={() => setAction('cancel')}
                className="drawer-btn"
                style={{
                  padding: '1rem 1.25rem',
                  background: '#FFFFFF',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontSize: '0.9375rem',
                  color: '#374151',
                  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#DC2626';
                  e.target.style.background = '#FEF2F2';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#D1D5DB';
                  e.target.style.background = '#FFFFFF';
                }}
              >
                <div style={{ fontWeight: '600', marginBottom: '0.25rem', color: '#111827' }}>Cancel & Schedule New</div>
                <div style={{ fontSize: '0.875rem', color: '#6B7280' }}>
                  Cancel the existing interview and schedule a new one with your current selections
                </div>
              </button>

              <button
                type="button"
                onClick={onSkip}
                className="drawer-btn"
                style={{
                  padding: '1rem 1.25rem',
                  background: '#F9FAFB',
                  border: '1px solid #E5E7EB',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontSize: '0.9375rem',
                  color: '#6B7280',
                  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#9CA3AF';
                  e.target.style.background = '#F3F4F6';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#E5E7EB';
                  e.target.style.background = '#F9FAFB';
                }}
              >
                <div style={{ fontWeight: '600', marginBottom: '0.25rem', color: '#374151' }}>Skip - Keep Existing</div>
                <div style={{ fontSize: '0.875rem', color: '#9CA3AF' }}>
                  Keep the existing interview as-is and close this dialog
                </div>
              </button>
            </div>
          </div>
        )}

        {action === 'reschedule' && (
          <div>
            <div style={{ fontWeight: '600', marginBottom: '1rem', color: '#111827' }}>
              Reschedule Interview
            </div>
            <div className="form-group">
              <label>New Date *</label>
              <input
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                required
              />
            </div>
            <div className="form-group">
              <label>New Time *</label>
              <input
                type="time"
                value={newTime}
                onChange={(e) => setNewTime(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label>Reason (Optional)</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows="2"
                placeholder="Why are you rescheduling this interview?"
              />
            </div>
          </div>
        )}

        {action === 'cancel' && (
          <div>
            <div style={{ fontWeight: '600', marginBottom: '1rem', color: '#111827' }}>
              Cancel Existing Interview
            </div>
            <div style={{
              background: '#FEF2F2',
              border: '1px solid #FEE2E2',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1rem'
            }}>
              <div style={{ color: '#991B1B', fontSize: '0.9rem' }}>
                <strong>Warning:</strong> Cancelling this interview will send cancellation emails to the candidate and interviewers.
              </div>
            </div>
            <div className="form-group">
              <label>Reason (Optional)</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows="2"
                placeholder="Why are you cancelling this interview?"
              />
            </div>
          </div>
        )}
      </div>
    </Drawer>
  );
};

/**
 * AssessmentAssigner Modal Component
 * Redesigned with profiling and recommendations
 */
const AssessmentAssigner = ({ application, jobId, onClose, onAssigned }) => {
  const [loading, setLoading] = useState(true);
  const [availableAssessments, setAvailableAssessments] = useState(null);
  const [selectedAssessmentId, setSelectedAssessmentId] = useState('auto-suggested-1');
  const [timeLimit, setTimeLimit] = useState(180); // 3 hours default
  const [deadline, setDeadline] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [templateSearchQuery, setTemplateSearchQuery] = useState('');
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [creatingTemplate, setCreatingTemplate] = useState(false); // eslint-disable-line no-unused-vars

  useEffect(() => {
    loadAvailableAssessments();
    
    // Set default deadline to 7 days from now
    const defaultDeadline = new Date();
    defaultDeadline.setDate(defaultDeadline.getDate() + 7);
    setDeadline(defaultDeadline.toISOString().slice(0, 16));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]); // Only depend on jobId, loadAvailableAssessments is stable

  const loadAvailableAssessments = async () => {
    setLoading(true);
    try {
      console.log('[AssessmentAssigner] Loading available assessments for jobId:', jobId);
      const response = await jobsAPI.getAvailableAssessments(jobId);
      console.log('[AssessmentAssigner] API Response:', response);
      console.log('[AssessmentAssigner] Response data:', response.data);
      
      if (!response.data) {
        console.error('[AssessmentAssigner] No data in response:', response);
        toastr.error('Invalid response from server');
        return;
      }
      
      setAvailableAssessments(response.data);
      
      // Default selection priority: Popular > Recommended Recruiter > Auto-generated
      let defaultAssessment = null;
      
      // First, check for popular assessments
      if (response.data.popularAssessments?.length > 0) {
        defaultAssessment = response.data.popularAssessments[0];
        console.log('[AssessmentAssigner] Using popular assessment:', defaultAssessment);
      }
      // Then check for recommended recruiter assessments
      if (!defaultAssessment && response.data.recruiterAssessments?.length > 0) {
        defaultAssessment = response.data.recruiterAssessments.find(a => a.isRecommended);
        if (defaultAssessment) {
          console.log('[AssessmentAssigner] Using recommended recruiter assessment:', defaultAssessment);
        }
      }
      // Fallback to auto-generated
      if (!defaultAssessment && response.data.suggestedAssessments?.length > 0) {
        defaultAssessment = response.data.suggestedAssessments[0];
        console.log('[AssessmentAssigner] Using auto-generated assessment:', defaultAssessment);
      }
      
      if (defaultAssessment) {
        setSelectedAssessmentId(defaultAssessment._id);
        setTimeLimit(defaultAssessment.timeLimit || defaultAssessment.estimatedTimeMinutes || 180);
      } else {
        console.warn('[AssessmentAssigner] No default assessment found, using auto-suggested-1');
        setSelectedAssessmentId('auto-suggested-1');
      }
    } catch (err) {
      console.error('[AssessmentAssigner] Failed to load available assessments:', err);
      console.error('[AssessmentAssigner] Error details:', err.response?.data || err.message);
      toastr.error('Failed to load assessment options: ' + (err.response?.data?.error || err.message));
      // Set to null so error state is shown
      setAvailableAssessments(null);
    } finally {
      setLoading(false);
    }
  };

  const handleAssessmentSelect = (assessmentId, assessment) => {
    setSelectedAssessmentId(assessmentId);
    if (assessment?.timeLimit) {
      setTimeLimit(assessment.timeLimit);
    }
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    if (!selectedAssessmentId) {
      toastr.error('Please select an assessment to assign');
      return;
    }
    
    setSubmitting(true);

    try {
      const payload = {
        timeLimit: parseInt(timeLimit),
        deadline: new Date(deadline).toISOString(),
        mode: 'async'
      };

      // If selected assessment is not auto-generated, include template ID
      if (selectedAssessmentId !== 'auto-suggested-1') {
        payload.assessmentTemplateId = selectedAssessmentId;
      }

      const response = await jobsAPI.assignAssessment(jobId, application._id, payload);

      toastr.success('Assessment assigned successfully!');
      toastr.info(`Assessment link: ${response.data.assessmentLink}`, { timeOut: 10000 });
      onAssigned();
    } catch (err) {
      console.error('Failed to assign assessment:', err);
      toastr.error(err.response?.data?.error || 'Failed to assign assessment');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      isOpen={true}
      onClose={onClose}
      title="Assign Technical Assessment"
      width="lg"
      footer={
        <>
          <button
            type="button"
            className="drawer-btn drawer-btn-secondary"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="drawer-btn drawer-btn-primary"
            onClick={handleAssign}
            disabled={submitting}
          >
            {submitting ? 'Assigning...' : 'Assign Assessment'}
          </button>
        </>
      }
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <p>Loading assessment options...</p>
        </div>
      ) : !availableAssessments ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#EF4444' }}>
          <p style={{ fontWeight: '600', marginBottom: '0.5rem' }}>Failed to load assessment options</p>
          <p style={{ fontSize: '0.875rem', color: '#6B7280' }}>
            Please check your browser console for details and try again.
          </p>
          <button
            onClick={loadAvailableAssessments}
            style={{
              marginTop: '1rem',
              padding: '0.5rem 1rem',
              background: '#3B82F6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      ) : (
        <form onSubmit={handleAssign}>
          {/* Job Context */}
          <div style={{ 
            background: '#F3F4F6', 
            padding: '1rem', 
            borderRadius: '8px', 
            marginBottom: '1.5rem',
            border: '1px solid #E5E7EB'
          }}>
            <div style={{ marginBottom: '0.5rem' }}>
              <strong style={{ color: '#374151' }}>Job Opening:</strong>
              <span style={{ color: '#6B7280', marginLeft: '0.5rem' }}>
                {availableAssessments.job.title}
              </span>
            </div>
            <div style={{ marginBottom: '0.5rem' }}>
              <strong style={{ color: '#374151' }}>Candidate:</strong>
              <span style={{ color: '#6B7280', marginLeft: '0.5rem' }}>
                {application.candidateInfo.firstName} {application.candidateInfo.lastName}
              </span>
            </div>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <div>
                <strong style={{ color: '#374151' }}>Level:</strong>
                <span style={{ 
                  color: '#6B7280', 
                  marginLeft: '0.5rem',
                  textTransform: 'capitalize'
                }}>
                  {availableAssessments.job.experienceLevel}
                </span>
              </div>
              {availableAssessments.job.department && (
                <div>
                  <strong style={{ color: '#374151' }}>Department:</strong>
                  <span style={{ color: '#6B7280', marginLeft: '0.5rem' }}>
                    {availableAssessments.job.department}
                  </span>
                </div>
              )}
              <div>
                <strong style={{ color: '#374151' }}>Suggested Difficulty:</strong>
                <span style={{ 
                  color: '#059669', 
                  marginLeft: '0.5rem',
                  fontWeight: '600',
                  textTransform: 'capitalize'
                }}>
                  {availableAssessments.job.suggestedDifficulty}
                </span>
              </div>
            </div>
          </div>

          {/* Recommended Assessments Section */}
          {((availableAssessments.popularAssessments?.length > 0) || 
            (availableAssessments.recruiterAssessments?.some(a => a.isRecommended))) && (
            <div style={{ marginBottom: '2rem' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem', 
                marginBottom: '1rem' 
              }}>
                <span style={{ fontSize: '1rem', fontWeight: '600', color: '#374151' }}>
                  Recommended Assessments
                </span>
                <span style={{ 
                  fontSize: '0.75rem', 
                  color: '#6B7280',
                  background: '#F3F4F6',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px'
                }}>
                  Based on similar {availableAssessments.job.experienceLevel} level positions
                </span>
              </div>
              
              {/* Popular Assessments */}
              {availableAssessments.popularAssessments?.map(ass => (
                <div
                  key={ass._id}
                  onClick={() => handleAssessmentSelect(ass._id, ass)}
                  style={{
                    border: selectedAssessmentId === ass._id 
                      ? '2px solid #3B82F6' 
                      : '1px solid #E5E7EB',
                    borderRadius: '8px',
                    padding: '1rem',
                    marginBottom: '0.75rem',
                    cursor: 'pointer',
                    background: selectedAssessmentId === ass._id ? '#EFF6FF' : 'white',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedAssessmentId !== ass._id) {
                      e.currentTarget.style.background = '#F9FAFB';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedAssessmentId !== ass._id) {
                      e.currentTarget.style.background = 'white';
                    }
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <input
                          type="radio"
                          checked={selectedAssessmentId === ass._id}
                          onChange={() => handleAssessmentSelect(ass._id, ass)}
                          style={{ cursor: 'pointer' }}
                        />
                        <strong style={{ color: '#1F2937', fontSize: '0.9375rem' }}>{ass.name}</strong>
                        <span style={{
                          background: '#10B981',
                          color: 'white',
                          fontSize: '0.75rem',
                          padding: '0.125rem 0.5rem',
                          borderRadius: '4px',
                          fontWeight: '600'
                        }}>
                          Popular
                        </span>
                        {ass.assignmentCount > 0 && (
                          <span style={{
                            background: '#F3F4F6',
                            color: '#6B7280',
                            fontSize: '0.75rem',
                            padding: '0.125rem 0.5rem',
                            borderRadius: '4px'
                          }}>
                            Used {ass.assignmentCount} time{ass.assignmentCount !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      <p style={{ 
                        margin: 0, 
                        color: '#6B7280', 
                        fontSize: '0.875rem',
                        marginLeft: '1.5rem'
                      }}>
                        {ass.description}
                      </p>
                      <div style={{ 
                        marginLeft: '1.5rem', 
                        marginTop: '0.5rem',
                        display: 'flex',
                        gap: '1rem',
                        fontSize: '0.8125rem',
                        color: '#9CA3AF'
                      }}>
                        <span>{ass.challengeCount} challenge{ass.challengeCount !== 1 ? 's' : ''}</span>
                        <span>{ass.timeLimit} min</span>
                        <span>{ass.difficulty}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Recommended Recruiter Assessments (only shown if no search query) */}
              {!templateSearchQuery && availableAssessments.recruiterAssessments
                ?.filter(a => a.isRecommended)
                .map(ass => (
                  <div
                    key={ass._id}
                    onClick={() => handleAssessmentSelect(ass._id, ass)}
                    style={{
                      border: selectedAssessmentId === ass._id 
                        ? '2px solid #3B82F6' 
                        : '1px solid #E5E7EB',
                      borderRadius: '8px',
                      padding: '1rem',
                      marginBottom: '0.75rem',
                      cursor: 'pointer',
                      background: selectedAssessmentId === ass._id ? '#EFF6FF' : 'white',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          <input
                            type="radio"
                            checked={selectedAssessmentId === ass._id}
                            onChange={() => handleAssessmentSelect(ass._id, ass)}
                            style={{ cursor: 'pointer' }}
                          />
                          <strong style={{ color: '#1F2937', fontSize: '0.9375rem' }}>{ass.name}</strong>
                          <span style={{
                            background: '#F59E0B',
                            color: 'white',
                            fontSize: '0.75rem',
                            padding: '0.125rem 0.5rem',
                            borderRadius: '4px',
                            fontWeight: '600'
                          }}>
                            Your Template
                          </span>
                          {ass.assignmentCount > 0 && (
                            <span style={{
                              background: '#F3F4F6',
                              color: '#6B7280',
                              fontSize: '0.75rem',
                              padding: '0.125rem 0.5rem',
                              borderRadius: '4px'
                            }}>
                              Used {ass.assignmentCount} time{ass.assignmentCount !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                        <p style={{ 
                          margin: 0, 
                          color: '#6B7280', 
                          fontSize: '0.875rem',
                          marginLeft: '1.5rem'
                        }}>
                          {ass.matchReason || ass.description}
                        </p>
                        <div style={{ 
                          marginLeft: '1.5rem', 
                          marginTop: '0.5rem',
                          display: 'flex',
                          gap: '1rem',
                          fontSize: '0.8125rem',
                          color: '#9CA3AF'
                        }}>
                          <span>{ass.challengeCount} challenge{ass.challengeCount !== 1 ? 's' : ''}</span>
                          <span>{ass.timeLimit} min</span>
                          <span>{ass.difficulty}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}

          {/* Create New Template Button */}
          <div style={{ marginBottom: '2rem' }}>
            <button
              type="button"
              onClick={() => setShowCreateTemplate(true)}
              style={{
                width: '100%',
                padding: '1rem',
                background: '#3B82F6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.9375rem',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                transition: 'all 0.2s',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#2563EB';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#3B82F6';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
              }}
            >
              <span>+</span>
              <span>Create New Assessment Template</span>
            </button>
          </div>

          {/* Your Assessment Templates - All Templates */}
          {availableAssessments.recruiterAssessments?.length > 0 && (
            <div style={{ marginBottom: '2rem' }}>
              <div style={{ 
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem'
              }}>
                <div style={{ 
                  fontSize: '0.9375rem', 
                  fontWeight: '600', 
                  color: '#374151'
                }}>
                  Your Assessment Templates
                  <span style={{ 
                    fontSize: '0.75rem', 
                    color: '#6B7280',
                    fontWeight: '400',
                    marginLeft: '0.5rem'
                  }}>
                    ({availableAssessments.recruiterAssessments.length} total)
                  </span>
                </div>
                <div style={{ 
                  position: 'relative',
                  width: '300px'
                }}>
                  <input
                    type="text"
                    placeholder="Search all templates..."
                    value={templateSearchQuery}
                    onChange={(e) => setTemplateSearchQuery(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.5rem 0.75rem',
                      paddingLeft: '2.25rem',
                      border: '1px solid #D1D5DB',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      outline: 'none'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#3B82F6';
                      e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#D1D5DB';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  {templateSearchQuery && (
                    <button
                      onClick={() => setTemplateSearchQuery('')}
                      style={{
                        position: 'absolute',
                        right: '0.5rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '1.125rem',
                        color: '#9CA3AF',
                        padding: '0.25rem',
                        lineHeight: '1'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#374151'}
                      onMouseLeave={(e) => e.target.style.color = '#9CA3AF'}
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
              {availableAssessments.recruiterAssessments
                .filter(a => {
                  if (!templateSearchQuery) return true;
                  const query = templateSearchQuery.toLowerCase();
                  return (
                    a.name?.toLowerCase().includes(query) ||
                    a.description?.toLowerCase().includes(query) ||
                    a.position?.toLowerCase().includes(query) ||
                    a.difficulty?.toLowerCase().includes(query)
                  );
                })
                .length === 0 && templateSearchQuery ? (
                  <div style={{
                    padding: '2rem',
                    textAlign: 'center',
                    color: '#9CA3AF',
                    fontSize: '0.875rem',
                    background: '#F9FAFB',
                    borderRadius: '8px',
                    border: '1px dashed #E5E7EB'
                  }}>
                    No templates found matching "{templateSearchQuery}"
                  </div>
                ) : (
                  availableAssessments.recruiterAssessments
                    .filter(a => {
                      if (!templateSearchQuery) return true;
                      const query = templateSearchQuery.toLowerCase();
                      return (
                        a.name?.toLowerCase().includes(query) ||
                        a.description?.toLowerCase().includes(query) ||
                        a.position?.toLowerCase().includes(query) ||
                        a.difficulty?.toLowerCase().includes(query)
                      );
                    })
                    .map(ass => (
                      <div
                        key={ass._id}
                        onClick={() => handleAssessmentSelect(ass._id, ass)}
                        style={{
                          border: selectedAssessmentId === ass._id 
                            ? '2px solid #3B82F6' 
                            : '1px solid #E5E7EB',
                          borderRadius: '8px',
                          padding: '1rem',
                          marginBottom: '0.75rem',
                          cursor: 'pointer',
                          background: selectedAssessmentId === ass._id ? '#EFF6FF' : 'white',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          if (selectedAssessmentId !== ass._id) {
                            e.currentTarget.style.background = '#F9FAFB';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedAssessmentId !== ass._id) {
                            e.currentTarget.style.background = 'white';
                          }
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                              <input
                                type="radio"
                                checked={selectedAssessmentId === ass._id}
                                onChange={() => handleAssessmentSelect(ass._id, ass)}
                                style={{ cursor: 'pointer' }}
                              />
                              <strong style={{ color: '#1F2937', fontSize: '0.9375rem' }}>{ass.name}</strong>
                              {ass.isRecommended && (
                                <span style={{
                                  background: '#F59E0B',
                                  color: 'white',
                                  fontSize: '0.75rem',
                                  padding: '0.125rem 0.5rem',
                                  borderRadius: '4px',
                                  fontWeight: '600'
                                }}>
                                  Recommended
                                </span>
                              )}
                            </div>
                            {ass.description && (
                              <p style={{ 
                                margin: 0, 
                                color: '#6B7280', 
                                fontSize: '0.875rem',
                                marginLeft: '1.5rem',
                                marginBottom: '0.5rem'
                              }}>
                                {ass.description}
                              </p>
                            )}
                            <div style={{
                        marginLeft: '1.5rem',
                        marginTop: '0.5rem',
                        display: 'flex',
                        gap: '1rem',
                        fontSize: '0.8125rem',
                        color: '#9CA3AF'
                      }}>
                              {ass.isRedTeam ? (
                                <span style={{ color: '#EF4444', fontWeight: '600' }}>Red Team Lab</span>
                              ) : (
                                <span>{ass.challengeCount} challenge{ass.challengeCount !== 1 ? 's' : ''}</span>
                              )}
                              <span>{ass.timeLimit} min</span>
                              <span>{ass.difficulty}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                )}
            </div>
          )}

          {/* Auto-Generated Suggestions */}
          {availableAssessments.suggestedAssessments?.length > 0 && (
            <div style={{ marginBottom: '2rem' }}>
              <div style={{ 
                fontSize: '0.9375rem', 
                fontWeight: '600', 
                color: '#374151',
                marginBottom: '1rem'
              }}>
                Auto-Generated Options
              </div>
              {availableAssessments.suggestedAssessments.map(ass => (
            <div
              key={ass._id}
              onClick={() => handleAssessmentSelect(ass._id, ass)}
              style={{
                border: selectedAssessmentId === ass._id 
                  ? '2px solid #3B82F6' 
                  : '1px solid #E5E7EB',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '0.75rem',
                cursor: 'pointer',
                background: selectedAssessmentId === ass._id ? '#EFF6FF' : 'white'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="radio"
                  checked={selectedAssessmentId === ass._id}
                  onChange={() => handleAssessmentSelect(ass._id, ass)}
                />
                <strong style={{ color: '#1F2937', fontSize: '0.875rem' }}>{ass.name}</strong>
                <span style={{
                  background: '#E5E7EB',
                  color: '#6B7280',
                  fontSize: '0.75rem',
                  padding: '0.125rem 0.5rem',
                  borderRadius: '4px'
                }}>
                  Auto-Generated
                </span>
                <span style={{ fontSize: '0.8125rem', color: '#9CA3AF', marginLeft: 'auto' }}>
                  ~{ass.estimatedChallenges} challenges • {ass.estimatedTimeMinutes} min
                </span>
              </div>
              <p style={{ margin: '0.5rem 0 0 1.5rem', color: '#6B7280', fontSize: '0.8125rem' }}>
                {ass.description}
              </p>
            </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {(!availableAssessments.popularAssessments?.length && 
            !availableAssessments.recruiterAssessments?.length && 
            !availableAssessments.suggestedAssessments?.length) && (
            <div style={{
              textAlign: 'center',
              padding: '2rem',
              background: '#F9FAFB',
              borderRadius: '8px',
              border: '1px dashed #D1D5DB',
              marginBottom: '2rem'
            }}>
              <p style={{ color: '#6B7280', marginBottom: '0.5rem' }}>
                No assessment templates available yet.
              </p>
              <p style={{ color: '#9CA3AF', fontSize: '0.875rem' }}>
                The auto-generated option will create a new assessment based on job requirements.
              </p>
            </div>
          )}

          {/* Configuration */}
          <div style={{ 
            borderTop: '2px solid #E5E7EB', 
            paddingTop: '1.5rem', 
            marginTop: '2rem' 
          }}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '500', 
                color: '#374151' 
              }}>
                Assessment Time Limit (minutes) *
              </label>
              <input
                type="number"
                value={timeLimit}
                onChange={(e) => setTimeLimit(e.target.value)}
                required
                min="30"
                max="480"
                style={{
                  width: '100%',
                  padding: '0.625rem',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '0.875rem'
                }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '500', 
                color: '#374151' 
              }}>
                Deadline *
              </label>
              <input
                type="datetime-local"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                required
                min={new Date().toISOString().slice(0, 16)}
                style={{
                  width: '100%',
                  padding: '0.625rem',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '0.875rem'
                }}
              />
            </div>

            <div style={{
              background: '#EFF6FF',
              border: '1px solid #BFDBFE',
              borderRadius: '6px',
              padding: '1rem',
              marginTop: '1rem'
            }}>
              <p style={{ margin: 0, color: '#1E40AF', fontSize: '0.875rem' }}>
                The candidate will receive an email with assessment details and access link.
              </p>
            </div>
          </div>
        </form>
      )}

      {/* Nested Drawer: Create New Assessment Template */}
      {showCreateTemplate && (
        <AssessmentTemplateCreator
          job={availableAssessments?.job}
          application={application}
          onClose={() => setShowCreateTemplate(false)}
          onCreated={(newTemplateId) => {
            setShowCreateTemplate(false);
            // Reload available assessments to include the new template
            loadAvailableAssessments().then(() => {
              // Auto-select the newly created template
              if (newTemplateId) {
                setSelectedAssessmentId(newTemplateId);
                toastr.success('Template created! It has been selected for you.');
              }
            });
          }}
        />
      )}
    </Drawer>
  );
};

/**
 * Assessment Template Creator Component
 * Nested drawer for creating a new assessment template
 */
const AssessmentTemplateCreator = ({ job, application, onClose, onCreated }) => {
  const [formData, setFormData] = useState({
    name: job ? `${job.title} - Template` : 'Assessment Template',
    position: job?.title || '',
    description: '',
    mode: 'async',
    timeLimit: 180, // minutes
    difficulty: job?.suggestedDifficulty || 'senior',
    challengeCount: 3,
    selectionMode: 'auto', // 'auto', 'manual', or 'red-team'
    selectedChallengeIds: [],
    scenario: 'ad-lateral-movement',
    hints: {
      enabled: true,
      maxPerChallenge: 3,
      penaltyPerHint: 5
    }
  });
  const [availableChallenges, setAvailableChallenges] = useState([]);
  const [loadingChallenges, setLoadingChallenges] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (formData.selectionMode === 'manual') {
      loadChallenges();
    }
  }, [formData.selectionMode]);

  const loadChallenges = async () => {
    setLoadingChallenges(true);
    try {
      const response = await challengesAPI.getAll({ isActive: true, availableOnly: true, _t: Date.now() });

      if (response.data?.challenges) {
        setAvailableChallenges(response.data.challenges);
      } else if (Array.isArray(response.data)) {
        setAvailableChallenges(response.data);
      }
    } catch (err) {
      console.error('[TemplateCreator] Failed to load challenges:', err);
      toastr.error('Failed to load challenges');
    } finally {
      setLoadingChallenges(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        return {
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: value
          }
        };
      }
      return { ...prev, [field]: value };
    });
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Template name is required';
    }
    if (!formData.position.trim()) {
      newErrors.position = 'Position is required';
    }
    if (formData.selectionMode === 'manual' && formData.selectedChallengeIds.length === 0) {
      newErrors.challenges = 'Please select at least one challenge';
    }
    if (formData.selectionMode === 'red-team' && !formData.scenario) {
      newErrors.scenario = 'Please select a scenario';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSubmitting(true);
    setErrors({});

    try {
      // Templates don't require a candidateId — they're reusable
      // If a candidate is linked, include it; otherwise create without
      const candidateId = application?.candidateId ||
                         (application?.candidateUserId?._id || application?.candidateUserId) ||
                         application?.candidate?._id ||
                         application?.candidate?.id;

      const templateAssessmentData = {
        name: formData.name,
        position: formData.position,
        ...(candidateId && { candidateId }),
        mode: formData.mode,
        status: 'draft', // Template is a draft
        scheduling: {
          timeLimit: formData.timeLimit * 60, // Convert to seconds
          availableFrom: new Date(),
          availableUntil: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000) // 1 year from now
        },
        challenges: formData.selectionMode === 'red-team'
          ? { selectionMode: 'red-team', scenario: formData.scenario, challengeIds: [] }
          : {
              selectionMode: formData.selectionMode,
              difficulty: formData.difficulty,
              count: formData.selectionMode === 'auto' ? formData.challengeCount : undefined,
              challengeIds: formData.selectionMode === 'manual' ? formData.selectedChallengeIds : []
            },
        hints: formData.hints,
        scoring: {
          method: 'automated',
          passThreshold: 70
        }
      };

      const response = await assessmentsAPI.create(templateAssessmentData);
      
      if (response.data?.assessment?._id) {
        toastr.success('Assessment template created successfully!');
        onCreated(response.data.assessment._id);
      } else {
        throw new Error('No assessment ID returned');
      }
    } catch (err) {
      console.error('[TemplateCreator] Failed to create template:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to create template';
      setErrors({ submit: errorMsg });
      toastr.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      isOpen={true}
      onClose={onClose}
      title="Create Assessment Template"
      width="lg"
      footer={
        <>
          <button
            type="button"
            className="drawer-btn drawer-btn-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="template-create-form"
            className="drawer-btn drawer-btn-primary"
            disabled={submitting}
          >
            {submitting ? 'Creating...' : 'Create Template'}
          </button>
        </>
      }
    >
      <form id="template-create-form" onSubmit={handleSubmit}>
        {errors.submit && (
          <div style={{
            padding: '1rem',
            background: '#FEE2E2',
            border: '1px solid #FECACA',
            borderRadius: '6px',
            marginBottom: '1.5rem',
            color: '#991B1B'
          }}>
            {errors.submit}
          </div>
        )}

        {/* Basic Info */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
            Template Name *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder="e.g., Senior Penetration Tester Assessment"
            required
            style={{
              width: '100%',
              padding: '0.625rem',
              border: errors.name ? '1px solid #EF4444' : '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '0.875rem'
            }}
          />
          {errors.name && (
            <p style={{ color: '#EF4444', fontSize: '0.75rem', marginTop: '0.25rem' }}>{errors.name}</p>
          )}
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
            Position *
          </label>
          <input
            type="text"
            value={formData.position}
            onChange={(e) => handleInputChange('position', e.target.value)}
            placeholder="e.g., Senior Security Engineer"
            required
            style={{
              width: '100%',
              padding: '0.625rem',
              border: errors.position ? '1px solid #EF4444' : '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '0.875rem'
            }}
          />
          {errors.position && (
            <p style={{ color: '#EF4444', fontSize: '0.75rem', marginTop: '0.25rem' }}>{errors.position}</p>
          )}
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
            Description (Optional)
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Describe this assessment template..."
            rows="3"
            style={{
              width: '100%',
              padding: '0.625rem',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '0.875rem',
              fontFamily: 'inherit'
            }}
          />
        </div>

        {/* Difficulty & Mode */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
              Difficulty *
            </label>
            <select
              value={formData.difficulty}
              onChange={(e) => handleInputChange('difficulty', e.target.value)}
              required
              style={{
                width: '100%',
                padding: '0.625rem',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '0.875rem'
              }}
            >
              <option value="junior">Junior</option>
              <option value="mid">Mid</option>
              <option value="senior">Senior</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
              Assessment Mode *
            </label>
            <select
              value={formData.mode}
              onChange={(e) => handleInputChange('mode', e.target.value)}
              required
              style={{
                width: '100%',
                padding: '0.625rem',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '0.875rem'
              }}
            >
              <option value="async">Async (Flexible timing)</option>
              <option value="timed">Timed (Fixed duration)</option>
              <option value="live">Live (Synchronized)</option>
            </select>
          </div>
        </div>

        {/* Time Limit */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
            Time Limit (minutes) *
          </label>
          <input
            type="number"
            value={formData.timeLimit}
            onChange={(e) => handleInputChange('timeLimit', parseInt(e.target.value) || 180)}
            min="30"
            max="480"
            required
            style={{
              width: '100%',
              padding: '0.625rem',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '0.875rem'
            }}
          />
        </div>

        {/* Challenge Selection Mode */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
            Challenge Selection *
          </label>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="radio"
                value="auto"
                checked={formData.selectionMode === 'auto'}
                onChange={(e) => handleInputChange('selectionMode', e.target.value)}
              />
              <span>Auto-select based on difficulty</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="radio"
                value="manual"
                checked={formData.selectionMode === 'manual'}
                onChange={(e) => handleInputChange('selectionMode', e.target.value)}
              />
              <span>Manually select challenges</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="radio"
                value="red-team"
                checked={formData.selectionMode === 'red-team'}
                onChange={(e) => handleInputChange('selectionMode', e.target.value)}
              />
              <span>Red Team Lab</span>
            </label>
          </div>
        </div>

        {/* Red Team Scenario Selection */}
        {formData.selectionMode === 'red-team' && (
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
              Scenario *
            </label>
            <select
              value={formData.scenario}
              onChange={(e) => handleInputChange('scenario', e.target.value)}
              style={{
                width: '100%',
                padding: '0.625rem',
                border: errors.scenario ? '1px solid #EF4444' : '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '0.875rem'
              }}
            >
              <option value="ad-lateral-movement">AD Lateral Movement — Kerberoasting, cron hijack, domain takeover</option>
            </select>
            {errors.scenario && (
              <p style={{ color: '#EF4444', fontSize: '0.75rem', marginTop: '0.25rem' }}>{errors.scenario}</p>
            )}
            <p style={{ color: '#6B7280', fontSize: '0.75rem', marginTop: '0.5rem' }}>
              A full lab environment (DC01, SRV01, WS01) will be provisioned from Assessment Details after creation.
            </p>
          </div>
        )}

        {/* Challenge Count (for auto mode) */}
        {formData.selectionMode === 'auto' && (
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
              Number of Challenges *
            </label>
            <input
              type="number"
              value={formData.challengeCount}
              onChange={(e) => handleInputChange('challengeCount', parseInt(e.target.value) || 3)}
              min="1"
              max="10"
              required
              style={{
                width: '100%',
                padding: '0.625rem',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '0.875rem'
              }}
            />
          </div>
        )}

        {/* Manual Challenge Selection */}
        {formData.selectionMode === 'manual' && formData.selectionMode !== 'red-team' && (
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
              Select Challenges *
            </label>
            {loadingChallenges ? (
              <p style={{ color: '#6B7280', fontSize: '0.875rem' }}>Loading challenges...</p>
            ) : availableChallenges.length > 0 ? (
              <div style={{
                maxHeight: '200px',
                overflowY: 'auto',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                padding: '0.5rem'
              }}>
                {availableChallenges.map(challenge => (
                  <label
                    key={challenge._id || challenge.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.5rem',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#F9FAFB'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <input
                      type="checkbox"
                      checked={formData.selectedChallengeIds.includes(challenge._id || challenge.id)}
                      onChange={(e) => {
                        const id = challenge._id || challenge.id;
                        if (e.target.checked) {
                          handleInputChange('selectedChallengeIds', [...formData.selectedChallengeIds, id]);
                        } else {
                          handleInputChange('selectedChallengeIds', formData.selectedChallengeIds.filter(cid => cid !== id));
                        }
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>
                        {challenge.title || challenge.name}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>
                        {challenge.difficulty} • {challenge.category || 'General'}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            ) : (
              <p style={{ color: '#6B7280', fontSize: '0.875rem' }}>No challenges available</p>
            )}
            {errors.challenges && (
              <p style={{ color: '#EF4444', fontSize: '0.75rem', marginTop: '0.25rem' }}>{errors.challenges}</p>
            )}
          </div>
        )}

        {/* Hints Configuration */}
        <div style={{
          background: '#F9FAFB',
          padding: '1rem',
          borderRadius: '6px',
          marginBottom: '1.5rem'
        }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', fontWeight: '500', color: '#374151' }}>
            <input
              type="checkbox"
              checked={formData.hints.enabled}
              onChange={(e) => handleInputChange('hints.enabled', e.target.checked)}
            />
            <span>Enable Hints</span>
          </label>
          
          {formData.hints.enabled && (
            <div style={{ marginLeft: '1.5rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: '#6B7280' }}>
                  Max Hints per Challenge
                </label>
                <input
                  type="number"
                  value={formData.hints.maxPerChallenge}
                  onChange={(e) => handleInputChange('hints.maxPerChallenge', parseInt(e.target.value) || 3)}
                  min="1"
                  max="10"
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '0.875rem'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: '#6B7280' }}>
                  Penalty per Hint (%)
                </label>
                <input
                  type="number"
                  value={formData.hints.penaltyPerHint}
                  onChange={(e) => handleInputChange('hints.penaltyPerHint', parseInt(e.target.value) || 5)}
                  min="0"
                  max="50"
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '0.875rem'
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </form>
    </Drawer>
  );
};

/**
 * GroupInterviewScheduler Modal Component
 * Handles scheduling interviews for multiple candidates at once
 */
const GroupInterviewScheduler = ({ selectedApplications, jobId, onClose, onScheduled }) => {
  const { user } = useAuth(); // Get current user to prevent self-selection
  const [interviewDate, setInterviewDate] = useState('');
  const [interviewTime, setInterviewTime] = useState('');
  const [interviewType, setInterviewType] = useState('video');
  const [meetingPlatform, setMeetingPlatform] = useState('teams');
  const [meetingMode, setMeetingMode] = useState('individual'); // 'group' or 'individual'
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedInterviewers, setSelectedInterviewers] = useState([]);
  const [interviewerQuery, setInterviewerQuery] = useState('');
  const [interviewerSuggestions, setInterviewerSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchingInterviewers, setSearchingInterviewers] = useState(false);
  const [frequentInterviewers, setFrequentInterviewers] = useState([]);
  const [loadingFrequent, setLoadingFrequent] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [autoGenerateMeeting, setAutoGenerateMeeting] = useState(true);

  // Load frequently used interviewers on mount
  useEffect(() => {
    const loadFrequentInterviewers = async () => {
      try {
        const response = await jobsAPI.getFrequentInterviewers(5);
        // BL-28: Filter out current recruiter from frequent interviewers
        const recruiterEmail = user?.email?.toLowerCase();
        const filtered = (response.data.interviewers || []).filter(
          i => i.interviewerEmail?.toLowerCase() !== recruiterEmail
        );
        setFrequentInterviewers(filtered);
      } catch (err) {
        console.error('[GroupInterviewScheduler] Failed to load frequent interviewers:', err);
      } finally {
        setLoadingFrequent(false);
      }
    };
    loadFrequentInterviewers();
  }, [user]);

  // Debounced interviewer search for interviewers
  useEffect(() => {
    if (interviewerQuery.trim().length < 2) {
      setInterviewerSuggestions([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setSearchingInterviewers(true);
        const response = await jobsAPI.searchAzureADUsers(interviewerQuery);
        // BL-28: Filter out current recruiter from search results
        const recruiterEmail = user?.email?.toLowerCase();
        const filteredUsers = (response.data.users || []).filter(
          u => u.email?.toLowerCase() !== recruiterEmail
        );
        setInterviewerSuggestions(filteredUsers);
        setShowSuggestions(true);
      } catch (err) {
        console.error('[GroupInterviewScheduler] Failed to search users:', err);
        setInterviewerSuggestions([]);
      } finally {
        setSearchingInterviewers(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [interviewerQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSchedule = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (selectedInterviewers.length === 0) {
        toastr.error('Please select at least one person to attend the interview');
        setSubmitting(false);
        return;
      }

      // BL-28: Frontend validation - prevent recruiter from selecting themselves
      const recruiterEmail = user?.email?.toLowerCase();
      const interviewers = selectedInterviewers.map(i => i.email);
      const selfSelected = interviewers.some(email => email.toLowerCase() === recruiterEmail);
      if (selfSelected) {
        toastr.error('You cannot schedule yourself as an interviewer. Please select a different person.');
        setSubmitting(false);
        return;
      }

      const scheduledDate = new Date(`${interviewDate}T${interviewTime}`);

      // Prepare interview data
      const interviewData = {
        scheduledDate: scheduledDate.toISOString(),
        type: interviewType,
        meetingPlatform: interviewType === 'video' ? meetingPlatform : null,
        autoGenerateMeeting: interviewType === 'video' && meetingPlatform === 'teams' && autoGenerateMeeting,
        location: interviewType === 'in-person' ? location : null,
        notes,
        duration: 60,
        interviewerEmails: interviewers,
        meetingMode: meetingMode // 'group' or 'individual'
      };

      if (meetingMode === 'group') {
        // Create ONE group meeting with all candidates
        const candidateEmails = selectedApplications.map(app => app.candidateInfo.email);
        const candidateNames = selectedApplications.map(app => 
          `${app.candidateInfo.firstName} ${app.candidateInfo.lastName}`
        ).join(', ');

        // Use first application's ID as primary, but pass all candidate info
        await jobsAPI.scheduleGroupInterview(jobId, {
          ...interviewData,
          applicationIds: selectedApplications.map(app => app._id),
          candidateEmails,
          candidateNames
        });

        toastr.success(`Group interview scheduled for ${selectedApplications.length} candidates!`);
      } else {
        // Create INDIVIDUAL meetings for each candidate
        const schedulePromises = selectedApplications.map(app =>
          jobsAPI.scheduleInterview(jobId, app._id, interviewData)
        );

        await Promise.all(schedulePromises);
        toastr.success(`Scheduled ${selectedApplications.length} individual interviews!`);
      }

      onScheduled();
    } catch (err) {
      console.error('[GroupInterviewScheduler] Failed to schedule:', err);
      toastr.error(err.response?.data?.error || 'Failed to schedule interviews');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      isOpen={true}
      onClose={onClose}
      title="Schedule Group Interview"
      width="lg"
      footer={
        <>
          <button
            type="button"
            className="drawer-btn drawer-btn-secondary"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="drawer-btn drawer-btn-primary"
            onClick={handleSchedule}
            disabled={submitting}
          >
            {submitting ? 'Scheduling...' : `Schedule for ${selectedApplications.length} Candidate(s)`}
          </button>
        </>
      }
    >
      {/* Selected Candidates Summary */}
      <div style={{
        background: '#F3F4F6',
        border: '1px solid #D1D5DB',
        borderRadius: '8px',
        padding: '1rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{ fontWeight: '600', marginBottom: '0.75rem', color: '#374151' }}>
          Selected Candidates ({selectedApplications.length}):
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
          {selectedApplications.map((app, index) => (
            <div
              key={index}
              style={{
                background: 'white',
                border: '1px solid #E5E7EB',
                borderRadius: '6px',
                padding: '0.375rem 0.75rem',
                fontSize: '0.875rem',
                color: '#111827'
              }}
            >
              {app.candidateInfo.firstName} {app.candidateInfo.lastName}
            </div>
          ))}
        </div>
      </div>

      {/* Meeting Mode Selection */}
      <div style={{
        background: '#FEF3C7',
        border: '2px solid #F59E0B',
        borderRadius: '8px',
        padding: '1rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{ fontWeight: '600', marginBottom: '0.75rem', color: '#92400E' }}>
          Interview Format
        </div>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.9rem' }}>
            <input
              type="radio"
              name="meetingMode"
              value="individual"
              checked={meetingMode === 'individual'}
              onChange={(e) => setMeetingMode(e.target.value)}
              style={{ marginRight: '0.5rem', cursor: 'pointer' }}
            />
            <strong>Individual Meetings</strong>
            <span style={{ marginLeft: '0.5rem', color: '#6B7280' }}>
              (Separate 1-on-1 for each candidate)
            </span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.9rem' }}>
            <input
              type="radio"
              name="meetingMode"
              value="group"
              checked={meetingMode === 'group'}
              onChange={(e) => setMeetingMode(e.target.value)}
              style={{ marginRight: '0.5rem', cursor: 'pointer' }}
            />
            <strong>Group Meeting</strong>
            <span style={{ marginLeft: '0.5rem', color: '#6B7280' }}>
              (All candidates together)
            </span>
          </label>
        </div>
        <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#64748B' }}>
          {meetingMode === 'individual' 
            ? 'Each candidate receives their own meeting link and calendar invite'
            : 'All candidates join the same meeting (recommended for presentations and group assessments)'}
        </div>
      </div>

      <form onSubmit={handleSchedule}>
        {/* Rest of the form fields - same as individual scheduler */}
        <div className="form-group">
          <label>Interview Date *</label>
          <input
            type="date"
            value={interviewDate}
            onChange={(e) => setInterviewDate(e.target.value)}
            required
            min={new Date().toISOString().split('T')[0]}
          />
        </div>

        <div className="form-group">
          <label>Interview Time *</label>
          <input
            type="time"
            value={interviewTime}
            onChange={(e) => setInterviewTime(e.target.value)}
            required
          />
        </div>

        {/* Interviewer Selection (same as individual scheduler) */}
        <div className="form-group">
          <label>Interviewer(s) *</label>
          
          {/* FIX UX-35: Loading State for Frequent Interviewers */}
          {loadingFrequent && (
            <div style={{ 
              marginBottom: '0.75rem', 
              padding: '0.75rem', 
              background: '#F9FAFB', 
              border: '1px solid #E5E7EB', 
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid #E5E7EB',
                borderTopColor: '#4F46E5',
                borderRadius: '50%',
              }}></div>
              <span style={{ color: '#6B7280', fontSize: '0.875rem' }}>
                Loading frequently used interviewers...
              </span>
            </div>
          )}
          
          {selectedInterviewers.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
              {selectedInterviewers.map((interviewer, index) => (
                <div
                  key={index}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    background: '#EFF6FF',
                    border: '1px solid #3B82F6',
                    borderRadius: '20px',
                    padding: '0.375rem 0.75rem',
                    fontSize: '0.875rem',
                    color: '#1E40AF',
                    gap: '0.5rem'
                  }}
                >
                  <span><strong>{interviewer.name}</strong></span>
                  <span style={{ color: '#6B7280', fontSize: '0.8rem' }}>({interviewer.email})</span>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedInterviewers(selectedInterviewers.filter((_, i) => i !== index));
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#EF4444',
                      cursor: 'pointer',
                      padding: '0',
                      fontSize: '1.1rem',
                      lineHeight: '1'
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          <div style={{ position: 'relative' }}>
            <input
              type="text"
              value={interviewerQuery}
              onChange={(e) => setInterviewerQuery(e.target.value)}
              onFocus={() => interviewerSuggestions.length > 0 && setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              placeholder="Type name or email to search..."
            />
            {searchingInterviewers && (
              <div style={{
                position: 'absolute',
                right: '10px',
                top: '0',
                bottom: '0',
                display: 'flex',
                alignItems: 'center',
                color: '#6B7280',
                fontSize: '0.875rem'
              }}>
                Searching...
              </div>
            )}

            {/* Dropdown - Shows frequent interviewers when no search query, search results when available */}
            {showSuggestions && (
              (interviewerQuery.trim() === '' && !loadingFrequent && frequentInterviewers.length > 0) ||
              (interviewerQuery.trim() !== '' && interviewerSuggestions.length > 0)
            ) && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: '0',
                right: '0',
                background: 'white',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                marginTop: '4px',
                maxHeight: '250px',
                overflowY: 'auto',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                zIndex: 1000
              }}>
                {/* Show frequent interviewers when no search query */}
                {interviewerQuery.trim() === '' && !loadingFrequent && frequentInterviewers.length > 0 && (
                  <>
                    <div style={{
                      padding: '0.5rem 1rem',
                      background: '#F0F9FF',
                      borderBottom: '1px solid #BFDBFE',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      color: '#1E40AF',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Frequently Used Interviewers
                    </div>
                    {frequentInterviewers.map((interviewer, index) => {
                      const alreadySelected = selectedInterviewers.some(i => i.email === interviewer.interviewerEmail);
                      return (
                        <div
                          key={`frequent-${index}`}
                          onClick={() => {
                            if (!alreadySelected) {
                              setSelectedInterviewers([...selectedInterviewers, {
                                id: interviewer.interviewerEmail,
                                name: interviewer.interviewerName,
                                email: interviewer.interviewerEmail,
                                jobTitle: interviewer.jobTitle,
                                department: interviewer.department
                              }]);
                              setInterviewerQuery('');
                              setShowSuggestions(false);
                            }
                          }}
                          style={{
                            padding: '0.75rem 1rem',
                            cursor: alreadySelected ? 'not-allowed' : 'pointer',
                            borderBottom: index < frequentInterviewers.length - 1 ? '1px solid #F3F4F6' : (interviewerSuggestions.length > 0 ? '1px solid #E5E7EB' : 'none'),
                            background: alreadySelected ? '#F3F4F6' : 'white',
                            opacity: alreadySelected ? 0.6 : 1
                          }}
                          onMouseEnter={(e) => !alreadySelected && (e.target.style.background = '#F9FAFB')}
                          onMouseLeave={(e) => !alreadySelected && (e.target.style.background = 'white')}
                        >
                          <div style={{ fontWeight: '600', color: '#111827', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            {interviewer.interviewerName}
                            {alreadySelected && <span style={{ 
                              background: '#D1FAE5',
                              color: '#065F46',
                              fontSize: '0.6875rem',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                              letterSpacing: '0.3px'
                            }}>Selected</span>}
                            {interviewer.timesSelectedByYou > 0 && (
                              <span style={{ 
                                fontSize: '0.7rem', 
                                background: '#3B82F6', 
                                color: 'white', 
                                padding: '1px 5px', 
                                borderRadius: '10px',
                                marginLeft: 'auto'
                              }}>
                                {interviewer.timesSelectedByYou}
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: '0.8rem', color: '#6B7280', marginTop: '0.1rem' }}>
                            {interviewer.interviewerEmail}
                          </div>
                          {(interviewer.jobTitle || interviewer.department) && (
                            <div style={{ fontSize: '0.75rem', color: '#9CA3AF', marginTop: '0.1rem' }}>
                              {interviewer.jobTitle || ''}
                              {interviewer.jobTitle && interviewer.department && ' • '}
                              {interviewer.department || ''}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}

                {/* Show search results when available */}
                {interviewerQuery.trim() !== '' && interviewerSuggestions.length > 0 && (
                  <>
                    {interviewerQuery.trim() !== '' && frequentInterviewers.length > 0 && (
                      <div style={{
                        padding: '0.5rem 1rem',
                        background: '#F9FAFB',
                        borderTop: '1px solid #E5E7EB',
                        borderBottom: '1px solid #E5E7EB',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        color: '#6B7280',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Search Results
                      </div>
                    )}
                    {interviewerSuggestions.map((user, index) => {
                  const alreadySelected = selectedInterviewers.some(i => i.email === user.email);
                  return (
                    <div
                      key={index}
                      onClick={() => {
                        if (!alreadySelected) {
                          setSelectedInterviewers([...selectedInterviewers, user]);
                          setInterviewerQuery('');
                          setInterviewerSuggestions([]);
                        }
                      }}
                      style={{
                        padding: '0.75rem 1rem',
                        cursor: alreadySelected ? 'not-allowed' : 'pointer',
                        borderBottom: index < interviewerSuggestions.length - 1 ? '1px solid #F3F4F6' : 'none',
                        background: alreadySelected ? '#F3F4F6' : 'white',
                        opacity: alreadySelected ? 0.6 : 1
                      }}
                      onMouseEnter={(e) => !alreadySelected && (e.target.style.background = '#F9FAFB')}
                      onMouseLeave={(e) => !alreadySelected && (e.target.style.background = 'white')}
                    >
                      <div style={{ fontWeight: '600', color: '#111827', fontSize: '0.9rem' }}>
                        {user.name} {alreadySelected && <span style={{ 
                          background: '#D1FAE5',
                          color: '#065F46',
                          fontSize: '0.6875rem',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          letterSpacing: '0.3px',
                          marginLeft: '0.5rem'
                        }}>Selected</span>}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#6B7280' }}>{user.email}</div>
                      {user.jobTitle && (
                        <div style={{ fontSize: '0.75rem', color: '#9CA3AF', marginTop: '0.1rem' }}>
                          {user.jobTitle}{user.department && ` • ${user.department}`}
                        </div>
                      )}
                    </div>
                  );
                    })}
                  </>
                )}
              </div>
            )}

            <small style={{ display: 'block', marginTop: '0.5rem', color: '#6B7280', fontSize: '0.85rem' }}>
              {selectedInterviewers.length === 0 
                ? (frequentInterviewers.length > 0 
                    ? 'Click to see frequently used interviewers, or type to search' 
                    : 'Start typing to search for interviewers')
                : `${selectedInterviewers.length} interviewer(s) selected`}
            </small>
          </div>
        </div>

        <div className="form-group">
          <label>Interview Type *</label>
          <select value={interviewType} onChange={(e) => setInterviewType(e.target.value)} required>
            <option value="video">Video Call</option>
            <option value="phone">Phone Call</option>
            <option value="in-person">In-Person</option>
          </select>
        </div>

        {interviewType === 'video' && (
          <>
            <div className="form-group">
              <label>Meeting Platform</label>
              <select
                value={meetingPlatform}
                onChange={(e) => {
                  setMeetingPlatform(e.target.value);
                  if (e.target.value === 'teams') {
                    setAutoGenerateMeeting(true);
                  } else {
                    setAutoGenerateMeeting(false);
                  }
                }}
              >
                <option value="teams">Microsoft Teams</option>
                <option value="google-meet">Google Meet</option>
                <option value="zoom">Zoom</option>
                <option value="other">Other</option>
              </select>
            </div>

          </>
        )}

        {interviewType === 'in-person' && (
          <div className="form-group">
            <label>Location</label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Office address..."
              required
            />
          </div>
        )}

        <div className="form-group">
          <label>Notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows="3"
            placeholder="Any additional notes for the candidates..."
          />
        </div>
      </form>
    </Drawer>
  );
};

export default ApplicationsList;
